import { jsPDF } from "jspdf";
import { rasterizeSignature } from "@/lib/signatures";
import { loadLogo, loadPdfBrand } from "@/lib/pdfLogo";
import { todayLocalIso } from "@/lib/utils";
import { API_BASE, api } from "@/lib/api";

// Per-site config cache so `hydrateSiteConfig()` doesn't hammer the API when
// the user downloads multiple PDFs from the same site in a row.
const _siteCfgCache = new Map();

/**
 * hydrateSiteConfig(item) — merges the site's white-label config (custom
 * PDF header logo, etc.) onto the report item BEFORE PDF generation.
 *
 * Report records from /api/inspections | /api/breakdowns | /api/hazards |
 * /api/handovers | /api/ppfs do NOT carry site config — so `loadPdfBrand()`
 * would otherwise always fall back to the default Pit Link mark.
 * Best-effort: on any error we return the item unchanged.
 */
async function hydrateSiteConfig(item) {
  const code = item?.site_code || item?.siteCode;
  if (!code) return item;
  if (item?.site_config || item?.custom_logo_data_url || item?.pdf_header_logo) return item;
  try {
    if (!_siteCfgCache.has(code)) {
      _siteCfgCache.set(code, (async () => {
        // The prod backend exposes site-config as `/site-config/me` (current
        // user's site) and `/admin/site-config?site_code=<CODE>` (owner
        // cross-site). Try user-scoped first (cheapest, no admin rights
        // needed for a supervisor downloading their own site's reports),
        // then fall back to admin lookup.
        try {
          const { data } = await api.get("/site-config/me");
          if (data && (data.site_code === code || data.code === code)) return data;
        } catch (ex) { /* try admin route */ }
        try {
          const { data } = await api.get("/admin/site-config", { params: { site_code: code } });
          if (data) return data;
        } catch (ex) {
          // eslint-disable-next-line no-console
          console.warn(`[reportPdf] site-config lookup failed for ${code}:`, ex?.response?.status || ex);
        }
        return null;
      })());
    }
    const cfg = await _siteCfgCache.get(code);
    if (cfg) return { ...item, site_config: cfg };
    // Retry on next attempt — don't burn the cache on a transient failure.
    _siteCfgCache.delete(code);
  } catch { /* silent */ }
  return item;
}

/**
 * Pit Link PDF generator — matches the mobile app's 1:1 PDF output.
 *
 * Public API:
 *   buildInspectionPdf(insp)  → jsPDF instance rendering an inspection
 *   downloadInspectionPdf(insp) → saves the PDF with the mobile app's filename convention
 *   buildReportPdf(item, kind) → back-compat wrapper (routes inspections through
 *     the new renderer, keeps a simple layout for other kinds)
 *   downloadReportPdf(item, kind)
 *   reportPdfBase64(item, kind)
 *
 * Visual reference:  Inspection-DRG456-2026-06-10.pdf (Josh's mobile export).
 *   • White paper, black text.
 *   • Subtle light-grey "S" watermark on every content page.
 *   • Header: circular "S" mark top-left · title "{TEMPLATE} — {ASSET_ID}" ·
 *     right-aligned "INSPECTION · COMPLIANCE PACK" + site code + generated timestamp.
 *   • Machine details as key-value list.
 *   • Colored stat row: OK / ISSUE / N/A / PPF / 75% WEAR / C/O.
 *   • Each template section rendered as:
 *       - Uppercase section heading in black
 *       - Optional intro paragraph
 *       - Table columns: ITEM · STATUS · COMPLETED BY · WHEN
 *       - Alternating row backgrounds, coloured status pills
 *       - Pin matrix items expanded to a 44-tile temperature grid + summary
 *   • Signature block (TRADESPERSON | SUPERVISOR).
 *   • Footer: "Pit Link Maintenance Hub" · page N of M.
 */

const M = 42;         // page margin (pt)
const HEADER_H = 148;
const CONTENT_TOP = 180;

// --- Stat / status palette (Material 100/700 shades used by the app) ---
const STATUS = {
  ok:     { bg: [220, 245, 224], text: [24, 118, 40],  label: "OK" },
  issue:  { bg: [253, 224, 224], text: [178, 34, 34],  label: "ISSUE" },
  na:     { bg: [232, 232, 232], text: [90, 90, 90],   label: "N/A" },
  ppf:    { bg: [219, 234, 254], text: [30, 64, 175],  label: "PLAN REQ" },
  wear75: { bg: [255, 224, 178], text: [178, 92, 12],  label: "75% WEAR" },
  co:     { bg: [232, 232, 232], text: [90, 90, 90],   label: "C/O" },
  open:   { bg: [244, 244, 244], text: [130, 130, 130], label: "OPEN" },
};

const TEMP = {
  ok:     { bg: [220, 245, 224], text: [24, 118, 40] },
  relube: { bg: [255, 224, 178], text: [178, 92, 12] },
  fail:   { bg: [253, 224, 224], text: [178, 34, 34] },
  empty:  { bg: [246, 246, 246], text: [140, 140, 140] },
};

// =========================================================================
// PHOTO PIPELINE — resolve any photo/markup shape to an embeddable PNG data
// URL. Handles:
//   • absolute http(s) URLs
//   • relative "/api/photos/{pid}" refs   → prefix with API_BASE
//   • data: URLs (pass through)
//   • blob: URLs (pass through)
//   • markup objects { base_image_uri, overlay_svg } → composite base + SVG
//     into a single flattened PNG so the technician's circled damage is
//     baked into the printed PDF (option (a) — Josh Feb 23).
// =========================================================================

function _absPhotoUrl(u) {
  if (!u || typeof u !== "string") return u;
  if (/^(https?:|data:|blob:)/i.test(u)) return u;
  if (u.startsWith("/")) return `${API_BASE}${u}`;
  return u;
}

function _photoOf(raw) {
  if (!raw) return { url: null, svg: null };
  if (typeof raw === "string") return { url: _absPhotoUrl(raw), svg: null };
  const url = raw.base_image_uri || raw.annotated_url || raw.marked_up_url ||
              raw.annotated || raw.markup_url || raw.overlay_url ||
              raw.image_url || raw.image || raw.url || raw.data ||
              raw.photo_url || raw.href || raw.src || null;
  const svg = raw.overlay_svg || raw.svg || raw.markup_svg || null;
  return { url: _absPhotoUrl(url), svg };
}

async function _fetchAsDataUrl(url) {
  if (!url) return null;
  if (typeof url === "string" && url.startsWith("data:")) return url;
  // Load via <img crossOrigin="anonymous"> and rasterise through a
  // canvas. This bypasses the CORS/cache issue that plagues fetch() —
  // browsers may have cached an earlier no-CORS response for the same
  // URL (Cloudflare's Vary: Origin isn't always honoured), which makes
  // fetch() error out. Image element requests are re-issued with the
  // right CORS mode when we pass crossOrigin="anonymous".
  try {
    // Cache-buster so we sidestep any stale non-CORS cached responses.
    const bust = url.includes("?") ? `${url}&_pdf=1` : `${url}?_pdf=1`;
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = "anonymous";
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = bust;
    });
    const w = img.naturalWidth || img.width || 800;
    const h = img.naturalHeight || img.height || 600;
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.85);
  } catch {
    return null;
  }
}

/**
 * _composeMarkup — fetch base photo, draw it into a canvas, then draw the
 * overlay SVG on top and return the flattened PNG data URL. Used so any
 * technician annotations (red circles, arrows) are baked into the PDF.
 */
async function _composeMarkup(baseUrl, overlaySvg) {
  const baseDataUrl = await _fetchAsDataUrl(baseUrl);
  if (!baseDataUrl) return null;
  if (!overlaySvg) return baseDataUrl;
  try {
    const baseImg = await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = baseDataUrl;
    });
    const w = baseImg.naturalWidth || baseImg.width || 800;
    const h = baseImg.naturalHeight || baseImg.height || 600;
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(baseImg, 0, 0, w, h);
    // Ensure the SVG carries width/height matching the canvas so it
    // rasterises at the correct size. viewBox / preserveAspectRatio
    // already come from the mobile app and give us a pixel-accurate
    // alignment.
    const svgSized = /<svg[^>]*\swidth=/.test(overlaySvg)
      ? overlaySvg
      : overlaySvg.replace(/<svg([^>]*)>/, `<svg$1 width="${w}" height="${h}">`);
    const svgUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgSized);
    const overlayImg = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = svgUrl;
    });
    ctx.drawImage(overlayImg, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.85);
  } catch {
    // Compositing failed — fall back to the base photo alone.
    return baseDataUrl;
  }
}

/**
 * _resolveEmbeddablePhotos — turn any list of photo-ish entries into a
 * flat list of { dataUrl, w, h, annotated, caption? } ready for
 * `doc.addImage`. Returns [] on empty / all-failed input.
 */
async function _resolveEmbeddablePhotos(rawList, { annotated = false, captions = null } = {}) {
  if (!Array.isArray(rawList) || !rawList.length) return [];
  const results = await Promise.all(rawList.map(async (raw, i) => {
    const { url, svg } = _photoOf(raw);
    if (!url) return null;
    const dataUrl = svg ? await _composeMarkup(url, svg) : await _fetchAsDataUrl(url);
    if (!dataUrl) return null;
    // Peek at natural dimensions so we can preserve aspect ratio when tiling.
    let w = 0, h = 0;
    try {
      const im = await new Promise((resolve, reject) => {
        const x = new Image();
        x.onload = () => resolve(x);
        x.onerror = reject;
        x.src = dataUrl;
      });
      w = im.naturalWidth || 800; h = im.naturalHeight || 600;
    } catch { w = 800; h = 600; }
    return { dataUrl, w, h, annotated: !!svg || annotated, caption: captions?.[i] || null };
  }));
  return results.filter(Boolean);
}

/**
 * _collectEvidencePhotos — collect every photo referenced by an item.
 *
 * • `photos` + `photo_urls`         → plain evidence photos
 * • `markups`                       → annotated photos (SVG overlays baked in)
 * • For inspections, walk `items_state[*].photos / markups` too so all
 *   technician-attached evidence lands in the PDF.
 *
 * Returns the raw list (not yet fetched) so callers can drive the async
 * fetch/compose pipeline via `_resolveEmbeddablePhotos`.
 */
function _collectEvidencePhotos(item) {
  const raw = [];
  const addList = (list) => { if (Array.isArray(list)) list.forEach((x) => x && raw.push(x)); };
  addList(item?.photos);
  addList(item?.photo_urls);
  addList(item?.attachments);
  addList(item?.markups);
  // Inspection: also fold per-item photos/markups so the "Evidence Photos"
  // pack covers everything the tradesperson attached anywhere in the sheet.
  const st = item?.items_state;
  if (st && typeof st === "object") {
    Object.values(st).forEach((s) => {
      if (!s) return;
      addList(s.photos);
      addList(s.markups);
    });
  }
  return raw;
}

/**
 * _appendEvidencePhotoPages — after the main body of any PDF, drop the
 * evidence photos into their own pages so supervisors reading the printed
 * pack always see them (Josh Feb 23: "if something gets a photo always
 * goes on PDF"). Tiles up to 4 photos per page (2×2) or one per page for
 * markup shots. Captions the annotated ones.
 */
function _appendEvidencePhotoPages(doc, photos, headerCtx, logo, {
  title = "EVIDENCE PHOTOS",
  fullPagePerPhoto = false,
} = {}) {
  if (!photos || !photos.length) return;
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const pageW = W - M * 2;
  const contentTop = CONTENT_TOP + 12;
  const footerReserve = 42;
  const availH = H - contentTop - footerReserve;

  const per = fullPagePerPhoto ? 1 : 4; // 4 on standard evidence pages
  const cols = fullPagePerPhoto ? 1 : 2;
  const rows = fullPagePerPhoto ? 1 : 2;
  const gap  = fullPagePerPhoto ? 0 : 10;
  const tileW = (pageW - gap * (cols - 1)) / cols;
  const tileH = (availH - gap * (rows - 1) - 24 /* section title */) / rows;

  for (let i = 0; i < photos.length; i += per) {
    doc.addPage();
    drawWatermark(doc);
    drawHeader(doc, headerCtx, logo || doc.__slLogo || null);
    // Section title on this page.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    doc.text(title, M, contentTop);
    doc.setDrawColor(230, 105, 22);
    doc.setLineWidth(1.5);
    doc.line(M, contentTop + 4, M + 60, contentTop + 4);

    const chunk = photos.slice(i, i + per);
    chunk.forEach((p, idx) => {
      const r = Math.floor(idx / cols);
      const c = idx % cols;
      const x = M + c * (tileW + gap);
      const y = contentTop + 20 + r * (tileH + gap);
      // Fit-inside letterbox so the whole image is visible (no crop).
      const scale = Math.min(tileW / p.w, tileH / p.h);
      const dw = p.w * scale;
      const dh = p.h * scale;
      const dx = x + (tileW - dw) / 2;
      const dy = y + (tileH - dh) / 2;
      // Background frame
      doc.setDrawColor(210, 210, 210);
      doc.setFillColor(248, 248, 248);
      doc.rect(x, y, tileW, tileH, "F");
      try {
        doc.addImage(p.dataUrl, "JPEG", dx, dy, dw, dh, undefined, "FAST");
      } catch { /* unsupported format — leave the frame */ }
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.5);
      doc.rect(x, y, tileW, tileH);
      // "MARKED" chip on annotated photos so the fault evidence stands out.
      if (p.annotated) {
        doc.setFillColor(230, 105, 22);
        doc.setDrawColor(230, 105, 22);
        doc.rect(x + 6, y + 6, 44, 12, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(255, 255, 255);
        doc.text("MARKED", x + 8, y + 14.5);
      }
      // Caption
      if (p.caption) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(90, 90, 90);
        doc.text(clip(p.caption, 60), x + 4, y + tileH + 10, { maxWidth: tileW - 8 });
      }
    });
    // NB: no drawFooter() here — the download function calls drawFooter
    // once at the very end so page numbers reflect the true total.
  }
}


// =========================================================================
// PUBLIC API
// =========================================================================

export function buildInspectionPdf(insp, rasterizedSigs = {}, logo = null) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  wrapWithSanitizer(doc);
  const brand = logo && logo.header ? logo : { header: logo, watermark: logo };
  doc.__slLogo = brand.header;        // header (may be site-specific)
  doc.__seWatermark = brand.watermark; // always Pit Link wordmark
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // Watermark first so everything draws on top.
  drawWatermark(doc);
  drawHeader(doc, insp, doc.__slLogo);

  let y = CONTENT_TOP;

  // ---- Title
  const titleTop = titleLine(insp);   // e.g. "DOZER TRACK & PIN INSPECTION — DOZ301"
  doc.setTextColor(15, 15, 15);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  const titleLines = doc.splitTextToSize(titleTop, W - M * 2 - 200);
  titleLines.forEach((ln, i) => doc.text(ln, M, y + i * 22));
  y += titleLines.length * 22 + 8;

  // Subtitle right block
  const nowStr = new Date().toLocaleString();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  const badge = "INSPECTION · COMPLIANCE PACK";
  doc.text(badge, W - M - doc.getTextWidth(badge), CONTENT_TOP);
  const site = String(insp.site_code || insp.site || "").toUpperCase();
  if (site) {
    doc.setFont("helvetica", "normal");
    doc.text(site, W - M - doc.getTextWidth(site), CONTENT_TOP + 14);
  }
  doc.setFontSize(8.5);
  doc.text(`Generated ${nowStr}`, W - M - doc.getTextWidth(`Generated ${nowStr}`), CONTENT_TOP + 28);

  // ---- Machine details
  y = drawMachineDetails(doc, M, y + 14, W, insp);

  // ---- Stat row
  y = drawStatRow(doc, M, y + 4, W, insp);

  // ---- Sections
  const sections = Array.isArray(insp.sections) && insp.sections.length
    ? insp.sections
    : (Array.isArray(insp.items) ? [{ name: "Inspection", items: insp.items }] : []);
  const itemsState = insp.items_state || {};

  sections.forEach((sec) => {
    y = ensureRoom(doc, y, insp, 90);
    y = drawSection(doc, M, y, W, sec, itemsState, insp);
  });

  // ---- Signature block (uses PRE-rasterised sigs so SVG blobs render properly)
  y = ensureRoom(doc, y, insp, 180, logo);
  drawSignatureBlock(doc, M, y + 10, W, insp, rasterizedSigs);

  // ---- Footer on all pages
  drawFooter(doc);

  return doc;
}

/**
 * Async entry point — rasterises SVG signatures (the shape the mobile app uses)
 * into PNG data URLs BEFORE the synchronous PDF build, so `.addImage()` can
 * embed them cleanly. Also loads the Pit Link wordmark asset for the header.
 */
export async function buildInspectionPdfAsync(insp) {
  insp = await hydrateSiteConfig(insp);
  const [trade, sup, logo] = await Promise.all([
    rasterizeSignature(insp.tradesperson_sig, { width: 512, bg: "#ffffff" }),
    rasterizeSignature(insp.supervisor_sig,   { width: 512, bg: "#ffffff" }),
    loadPdfBrand(insp),
  ]);
  // Pre-resolve per-item photos into JPEG data URLs so `drawSection` can
  // paint them INLINE right under the item block where the issue was
  // raised (Josh Feb 23: "put under that but not at the end of PDF").
  // Doing the fetch/compose here — while everything else is async —
  // keeps `buildInspectionPdf` a pure synchronous jsPDF renderer.
  const itemsState = insp.items_state || {};
  const inlineJobs = Object.entries(itemsState).map(async ([iid, st]) => {
    if (!st) return;
    const photosRaw = Array.isArray(st.photos) ? st.photos : [];
    const markupsRaw = Array.isArray(st.markups) ? st.markups : [];
    if (!photosRaw.length && !markupsRaw.length) return;
    // Markups first so the annotated shots print above the raw shots.
    const [mk, ph] = await Promise.all([
      _resolveEmbeddablePhotos(markupsRaw, { annotated: true }),
      _resolveEmbeddablePhotos(photosRaw),
    ]);
    const merged = [];
    const seen = new Set();
    [...mk, ...ph].forEach((p) => {
      if (!p?.dataUrl || seen.has(p.dataUrl)) return;
      seen.add(p.dataUrl);
      merged.push(p);
    });
    st.__inlinePhotos = merged;
    // Also strip this item from the top-level collect so the annex
    // (used only if this feature ever regresses) doesn't double-print.
    st.__inlinePhotosConsumed = true;
    // Mutate `insp.items_state` in place — buildInspectionPdf reads it.
    insp.items_state[iid] = st;
  });
  await Promise.all(inlineJobs);
  return buildInspectionPdf(insp, { trade, sup }, logo);
}

export async function downloadInspectionPdf(insp) {
  // Photos are now rendered INLINE under each item (see
  // buildInspectionPdfAsync + drawInlineItemPhotos). No more end-of-PDF
  // evidence annex — the photo sits next to the issue it documents,
  // which is what supervisors need when signing off (Josh Feb 23).
  const doc = await buildInspectionPdfAsync(insp);
  drawFooter(doc);
  const dt = todayLocalIso(new Date(insp.approved_at || insp.updated_at || insp.started_at || Date.now()));
  const asset = String(insp.asset_id || insp.asset_name || "record").replace(/[^A-Za-z0-9_-]+/g, "");
  doc.save(`Inspection-${asset}-${dt}.pdf`);
  return `Inspection-${asset}-${dt}.pdf`;
}

// --- Legacy generic wrapper kept so non-inspection callers still work -----
export function buildReportPdf(item, kind = "Report", rasterizedSigs = {}, logo = null) {
  const k = String(kind).toLowerCase();
  if (k.includes("inspection") && (item?.sections || item?.items_state)) {
    return buildInspectionPdf(item, rasterizedSigs, logo);
  }
  if (k.includes("breakdown")) {
    return buildBreakdownPdf(item, rasterizedSigs, logo);
  }
  if (k === "ppf" || k.includes("ppf") || k.includes("preventive")) {
    return buildPPFPdf(item, rasterizedSigs, logo);
  }
  // Fallback: minimal single-page render for any other kinds.
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  wrapWithSanitizer(doc);
  const brand = logo && logo.header ? logo : { header: logo, watermark: logo };
  doc.__slLogo = brand.header;
  doc.__seWatermark = brand.watermark;
  const W = doc.internal.pageSize.getWidth();
  drawWatermark(doc);
  drawHeader(doc, { template_name: kind, site_code: item?.site_code, asset_id: item?.asset_id }, doc.__slLogo);
  let y = CONTENT_TOP;
  doc.setTextColor(15, 15, 15);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  // Josh (Feb 26): distinct professional titles per report type. Every
  // exported PDF now has its own identity.
  let headline = String(kind).toUpperCase();
  if (k.includes("handover")) headline = "OPERATIONAL SHIFT HANDOVER";
  if (k.includes("hazard"))   headline = "SITE HAZARD NOTIFICATION";
  doc.text(headline, M, y);
  y += 24;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(50, 50, 50);

  // For Handovers, render the narrative fields as titled blocks so the
  // printed doc mirrors what the tradesman filled in on-screen. Josh
  // (Feb 26): rename to Operational Shift Handover.
  if (k.includes("handover")) {
    const blocks = [
      ["Fleet Unit",                    item?.asset_id],
      ["Outgoing Crew",                 item?.outgoing_crew || item?.crew_name || item?.tradesman],
      ["Incoming Crew",                 item?.incoming_crew],
      ["Shift · Date",                  [item?.shift, fmtWhen(item?.date || item?.created_at)].filter(Boolean).join(" · ")],
      ["SMU (hours)",                   item?.smu],
      ["Equipment Status Summary",      item?.machine_status || item?.equipment_status],
      ["Outstanding Tasks",             item?.outstanding_tasks || item?.delays_parts],
      ["Priority Actions for Next Shift", item?.next_priorities || item?.priority_actions],
      ["Safety Alerts Active",          item?.safety_issues || item?.safety_alerts],
      ["Breakdown Fault",               item?.breakdown_fault],
      ["Work Completed",                item?.work_completed],
      ["Operator Complaint",            item?.operator_complaint],
    ];
    blocks.forEach(([label, value]) => {
      const v = value != null && value !== "" ? String(value) : null;
      if (!v) return;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      doc.text(label.toUpperCase(), M, y);
      y += 14;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.5);
      doc.setTextColor(15, 15, 15);
      const lines = doc.splitTextToSize(v, W - M * 2);
      lines.forEach((ln) => { doc.text(ln, M, y); y += 14; });
      y += 6;
    });
  } else if (k.includes("hazard")) {
    // Structured hazard block — matches the new portal wording.
    const blocks = [
      ["Location",                        item?.location],
      ["Hazard Classification",           item?.classification || item?.category],
      ["Risk Assessment Level",           item?.risk_level || item?.severity],
      ["Area Isolation Required",         item?.area_isolation_required === true ? "Yes" : (item?.area_isolation_required === false ? "No" : null)],
      ["Shift",                           item?.shift],
      ["Responsible Person",              item?.tradesman_name || item?.created_by_name || item?.reported_by],
      ["Assigned Supervisor",             item?.assigned_supervisor_name || item?.supervisor_name],
      ["Reported At",                     fmtWhen(item?.created_at || item?.reported_at)],
      ["Risk Description",                item?.description || item?.summary],
      ["Immediate Controls Implemented",  item?.immediate_controls || item?.controls_implemented],
      ["Mitigation Strategy",             item?.corrective_action || item?.suggested_corrective],
      ["Notes",                           item?.notes],
    ];
    blocks.forEach(([label, value]) => {
      const v = value != null && value !== "" ? String(value) : null;
      if (!v) return;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      doc.text(label.toUpperCase(), M, y);
      y += 14;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.5);
      doc.setTextColor(15, 15, 15);
      const lines = doc.splitTextToSize(v, W - M * 2);
      lines.forEach((ln) => { doc.text(ln, M, y); y += 14; });
      y += 6;
    });
  } else {
    const desc = item?.description || item?.notes || item?.summary || "";
    if (desc) {
      const lines = doc.splitTextToSize(String(desc), W - M * 2);
      lines.forEach((ln) => { doc.text(ln, M, y); y += 14; });
    }
  }
  drawFooter(doc);
  return doc;
}

export async function downloadReportPdf(item, kind) {
  const k = String(kind || "").toLowerCase();
  if (k.includes("inspection")) return downloadInspectionPdf(item);
  if (k.includes("breakdown")) return downloadBreakdownPdf(item);
  if (k === "ppf" || k.includes("ppf") || k.includes("preventive")) return downloadPPFPdf(item);
  if (k.includes("shift") || k.includes("summary")) return downloadShiftSummaryPdf(item);
  if (k.includes("handover")) return downloadHandoverPdf(item);
  if (k.includes("hazard")) return downloadHazardPdf(item);
  const doc = buildReportPdf(item, kind);
  const filename = `${(kind || "report").toLowerCase().replace(/\s+/g, "-")}-${(item?.id || item?._id || "record").toString().slice(0, 8)}.pdf`;
  doc.save(filename);
  return filename;
}

/**
 * downloadHandoverPdf — builds the Handover PDF WITH the Pit Link logo
 * header (Josh Feb 23: "PDF for handover doesnt have any of the new logo
 * in it") and appends every attached photo as an evidence page.
 */
export async function downloadHandoverPdf(item) {
  item = await hydrateSiteConfig(item); const logo = await loadPdfBrand(item);
  const doc = buildReportPdf(item, "Handover", {}, logo);
  drawFooter(doc);
  try {
    const rawPhotos = _collectEvidencePhotos(item);
    const photos = await _resolveEmbeddablePhotos(rawPhotos);
    if (photos.length) {
      _appendEvidencePhotoPages(doc, photos, {
        template_name: "Handover", site_code: item?.site_code, asset_id: item?.asset_id,
      }, logo, { title: "EVIDENCE PHOTOS" });
      drawFooter(doc);
    }
  } catch { /* non-blocking */ }
  const dt = todayLocalIso(new Date(item?.created_at || Date.now()));
  const asset = String(item?.asset_id || "handover").replace(/[^A-Za-z0-9_-]+/g, "");
  const filename = `Handover-${asset}-${dt}.pdf`;
  doc.save(filename);
  return filename;
}

/**
 * downloadHazardPdf — the generic fallback path lacked the logo and
 * skipped photos. This wrapper mirrors Handover's approach: load the
 * logo, use the generic Hazard renderer for the text sections, then
 * append the evidence photo annex.
 */
export async function downloadHazardPdf(item) {
  item = await hydrateSiteConfig(item); const logo = await loadPdfBrand(item);
  const doc = buildReportPdf(item, "Hazard", {}, logo);
  drawFooter(doc);
  try {
    const rawPhotos = _collectEvidencePhotos(item);
    const photos = await _resolveEmbeddablePhotos(rawPhotos);
    if (photos.length) {
      _appendEvidencePhotoPages(doc, photos, {
        template_name: "Hazard", site_code: item?.site_code, asset_id: item?.asset_id,
      }, logo, { title: "EVIDENCE PHOTOS" });
      drawFooter(doc);
    }
  } catch { /* non-blocking */ }
  const dt = todayLocalIso(new Date(item?.created_at || Date.now()));
  const asset = String(item?.asset_id || item?.zone || "hazard").replace(/[^A-Za-z0-9_-]+/g, "");
  const filename = `Hazard-${asset}-${dt}.pdf`;
  doc.save(filename);
  return filename;
}

export async function reportPdfBase64(item, kind) {
  const k = String(kind || "").toLowerCase();
  let doc;
  if (k.includes("inspection")) {
    doc = await buildInspectionPdfAsync(item);
  } else if (k.includes("breakdown")) {
    doc = await buildBreakdownPdfAsync(item);
  } else if (k === "ppf" || k.includes("ppf") || k.includes("preventive")) {
    doc = await buildPPFPdfAsync(item);
  } else {
    // Handover / Hazard / Anything else: load the logo so the email PDF
    // matches the downloaded PDF (Josh Feb 23 handover-logo fix).
    item = await hydrateSiteConfig(item); const logo = await loadPdfBrand(item);
    doc = buildReportPdf(item, kind, {}, logo);
  }
  // Append evidence photos to the emailed PDF too — parity with the
  // downloaded version so no attachment is missing from the emailed pack.
  try {
    const rawPhotos = _collectEvidencePhotos(item);
    // PPF parts book photos also get the full-page treatment for email.
    if ((k === "ppf" || k.includes("ppf")) && Array.isArray(item?.parts_book_photos) && item.parts_book_photos.length) {
      const pbook = await _resolveEmbeddablePhotos(item.parts_book_photos);
      if (pbook.length) {
        _appendEvidencePhotoPages(doc, pbook, item, doc.__slLogo, {
          title: "PARTS BOOK PHOTO", fullPagePerPhoto: true,
        });
      }
    }
    const photos = await _resolveEmbeddablePhotos(rawPhotos);
    if (photos.length) {
      _appendEvidencePhotoPages(doc, photos, item, doc.__slLogo, { title: "EVIDENCE PHOTOS" });
    }
    // Re-draw footer once at the end so page numbers reflect the final total.
    drawFooter(doc);
  } catch { /* non-blocking */ }
  const blob = doc.output("blob");
  const buf = await blob.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

// =========================================================================
// SHIFT SUMMARY PDF
// =========================================================================
export function buildShiftSummaryPdf(payload, logo = null) {
  const {
    date, shift, weekNo, siteCode,
    stats = {},
    handovers = [],
    breakdowns = [],
    completedInspections = [],
    completedPpfs = [],
    customJobs = [],
    hazards = [],
    wearRows = [],
  } = payload || {};
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  wrapWithSanitizer(doc);
  const brand = logo && logo.header ? logo : { header: logo, watermark: logo };
  doc.__slLogo = brand.header;
  doc.__seWatermark = brand.watermark;
  const W = doc.internal.pageSize.getWidth();
  const shiftLabel = String(shift || "day").toUpperCase();
  const meta = {
    template_name: `Shift Summary — ${shiftLabel}`,
    site_code: siteCode || "",
    asset_id: "",
  };
  drawWatermark(doc);
  drawHeader(doc, meta, doc.__slLogo);

  // Title block
  let y = CONTENT_TOP;
  doc.setTextColor(15, 15, 15);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("SHIFT SUMMARY", M, y);
  doc.setFontSize(11);
  doc.setTextColor(90, 90, 90);
  doc.setFont("helvetica", "normal");
  const dateStr = date ? fmtDayLong(date) : "";
  doc.text(`${dateStr}  ·  ${shiftLabel} SHIFT  ·  WEEK ${weekNo || ""}  ·  ${siteCode || ""}`, M, y + 18);
  y += 40;

  // Counts strip
  const counts = [
    { label: "Handovers", value: pick(stats, ["handovers", "handover_count"]) ?? handovers.length },
    { label: "Breakdowns", value: pick(stats, ["breakdowns", "breakdown_count"]) ?? breakdowns.length },
    { label: "Inspections", value: pick(stats, ["inspections", "inspection_count"]) ?? completedInspections.length },
    { label: "Planning Requests", value: pick(stats, ["ppfs", "ppf_count"]) ?? completedPpfs.length },
    { label: "Custom Jobs", value: pick(stats, ["custom_jobs", "customjobs_count"]) ?? customJobs.length },
    { label: "Hazards", value: pick(stats, ["hazards", "hazard_count"]) ?? hazards.length },
  ];
  y = drawCountsStrip(doc, M, y, W, counts);

  // Handovers — Crew + Date only per client brief. The full handover
  // narrative (work_completed, machine_status, hazards, etc.) is retained on
  // the mobile app; this line is just an audit trail of who was on shift.
  y = ensureRoomSimple(doc, y, meta, 80, logo);
  y = drawSectionHeader(doc, M, y, "SHIFT HANDOVER · CREW ON SHIFT");
  if (handovers.length === 0) {
    y = drawMuted(doc, M, y, "No handovers logged this shift.");
  } else {
    handovers.forEach((h) => {
      y = ensureRoomSimple(doc, y, meta, 40, logo);
      y = drawHandoverBlock(doc, M, y, W, h);
    });
  }
  y += 6;

  // Inspections completed — Work Order # prepended when present for audit.
  y = ensureRoomSimple(doc, y, meta, 80, logo);
  y = drawSectionHeader(doc, M, y, "INSPECTIONS COMPLETED");
  if (completedInspections.length === 0) {
    y = drawMuted(doc, M, y, "No inspections signed off this shift.");
  } else {
    y = drawListRows(doc, M, y, W, completedInspections.map((i) => ({
      title: i.template_name || i.title || "Inspection",
      asset: i.asset_id || "",
      wo: i.work_order_number || "",
      who: i.supervisor_name || i.approved_by || "—",
      when: fmtWhen(i.approved_at || i.updated_at),
    })));
  }
  y += 6;

  // Planning Requests completed
  y = ensureRoomSimple(doc, y, meta, 80, logo);
  y = drawSectionHeader(doc, M, y, "PLANNING REQUESTS COMPLETED");
  if (completedPpfs.length === 0) {
    y = drawMuted(doc, M, y, "No Planning Requests completed this shift.");
  } else {
    y = drawListRows(doc, M, y, W, completedPpfs.map((p) => ({
      title: p.fault || p.title || "Planning Request",
      asset: p.asset_id || "",
      wo: p.work_order_number || "",
      who: p.completed_by_name || p.supervisor_name || "—",
      when: fmtWhen(p.completed_at || p.updated_at),
    })));
  }
  y += 6;

  // Custom Jobs completed (asset-demands signed off during this shift)
  y = ensureRoomSimple(doc, y, meta, 80, logo);
  y = drawSectionHeader(doc, M, y, "CUSTOM JOBS COMPLETED");
  if (customJobs.length === 0) {
    y = drawMuted(doc, M, y, "No custom jobs signed off this shift.");
  } else {
    y = drawListRows(doc, M, y, W, customJobs.map((c) => ({
      title: c.text || c.title || c.description || "Custom job",
      asset: c.asset_id || "",
      wo: c.work_order_number || c.values?.work_order_number || "",
      who: c.completed_by_name || c.signed_by_name || "—",
      when: fmtWhen(c.completed_at || c.closed_at || c.signed_at),
    })));
  }
  y += 6;

  // Safety Hazards this shift (pending + signed-off)
  y = ensureRoomSimple(doc, y, meta, 80, logo);
  y = drawSectionHeader(doc, M, y, "SITE HAZARDS");
  if (hazards.length === 0) {
    y = drawMuted(doc, M, y, "No hazards this shift.");
  } else {
    y = drawListRows(doc, M, y, W, hazards.map((h) => {
      const done = String(h.status || "").toLowerCase() === "actioned" || String(h.status || "").toLowerCase() === "closed";
      return {
        title: `${(h.risk_level || "hazard").toUpperCase()} · ${h.description || h.title || "Hazard"}`,
        asset: h.location || "",
        who: done ? (h.actioned_by || "signed off") : "PENDING SIGN-OFF",
        when: fmtWhen(done ? (h.actioned_at || h.closed_at) : h.created_at),
      };
    }));
  }
  y += 6;

  // Breakdowns
  y = ensureRoomSimple(doc, y, meta, 80, logo);
  y = drawSectionHeader(doc, M, y, "ASSET BREAKDOWNS · THIS SHIFT");
  if (breakdowns.length === 0) {
    y = drawMuted(doc, M, y, "No breakdowns this shift.");
  } else {
    y = drawListRows(doc, M, y, W, breakdowns.map((b) => ({
      title: b.complaint || b.cause || b.title || "Breakdown",
      asset: b.asset_id || "",
      wo: b.work_order_number || "",
      who: b.employee_name || "—",
      when: fmtWhen(b.breakdown_time || b.created_at),
    })));
  }
  y += 6;

  // Digger teeth % worn
  if (wearRows.length > 0) {
    y = ensureRoomSimple(doc, y, meta, 80, logo);
    y = drawSectionHeader(doc, M, y, "TEETH PERCENTAGE WORN");
    y = drawWearTable(doc, M, y, W, wearRows);
  }

  drawFooter(doc);
  return doc;
}

export async function downloadShiftSummaryPdf(payload) {
  payload = await hydrateSiteConfig(payload); const logo = await loadPdfBrand(payload);
  const doc = buildShiftSummaryPdf(payload, logo);
  const dt = String(payload?.date || todayLocalIso());
  const shift = String(payload?.shift || "day").toUpperCase();
  const site = String(payload?.siteCode || "site").replace(/[^A-Za-z0-9_-]+/g, "");
  const filename = `ShiftSummary-${site}-${dt}-${shift}.pdf`;
  doc.save(filename);
  return filename;
}

/* ---- helpers unique to Shift Summary PDF ---- */
function pick(obj, keys) {
  for (const k of keys) if (obj?.[k] != null) return Number(obj[k]);
  return null;
}
function fmtDayLong(d) {
  try { return new Date(d).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long", year: "numeric" }); }
  catch { return String(d || ""); }
}
function fmtWhen(v) {
  if (!v) return "";
  try { return new Date(v).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(v); }
}
function drawCountsStrip(doc, M, y, W, counts) {
  const gap = 8;
  const tileW = (W - M * 2 - gap * (counts.length - 1)) / counts.length;
  counts.forEach((c, i) => {
    const x = M + i * (tileW + gap);
    doc.setFillColor(250, 250, 250);
    doc.setDrawColor(228, 228, 228);
    doc.setLineWidth(0.4);
    doc.rect(x, y, tileW, 46, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(130, 130, 130);
    doc.text(String(c.label).toUpperCase(), x + 8, y + 14);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(20, 20, 20);
    doc.text(String(c.value ?? 0), x + 8, y + 38);
  });
  return y + 60;
}
function drawMuted(doc, M, y, text) {
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.setTextColor(140, 140, 140);
  doc.text(String(text), M, y + 12);
  return y + 22;
}
function drawHandoverBlock(doc, M, y, W, h) {
  // Handover PDF row — deliberately minimal per client brief:
  // just Crew and Date. All other handover detail lives on the mobile app
  // and does not go on the printed shift summary.
  const tableW = W - M * 2;
  const rowH = 26;
  doc.setFillColor(250, 250, 250);
  doc.setDrawColor(228, 228, 228);
  doc.setLineWidth(0.4);
  doc.rect(M, y, tableW, rowH, "FD");
  const crew = h.assigned_crew_name || h.crew_name || h.crew_id || h.tradesman || h.employee_name || "—";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  doc.text(String(crew), M + 10, y + 17);
  const when = fmtWhen(h.created_at || h.date);
  if (when) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.text(when, M + tableW - 10 - doc.getTextWidth(when), y + 17);
  }
  return y + rowH + 6;
}
function drawListRows(doc, M, y, W, rows) {
  // Row layout — asset (bold) · title · WO# pill · signed-by · timestamp.
  // Rows auto-expand to two lines when the title or WO# is too long so the
  // audit trail never gets truncated to a single character.
  const tableW = W - M * 2;
  const rowH = 30;
  rows.forEach((r, i) => {
    doc.setFillColor(i % 2 === 0 ? 250 : 245, i % 2 === 0 ? 250 : 245, i % 2 === 0 ? 250 : 245);
    doc.rect(M, y, tableW, rowH, "F");
    doc.setDrawColor(228, 228, 228);
    doc.setLineWidth(0.4);
    doc.rect(M, y, tableW, rowH);
    // Left column — asset + title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(20, 20, 20);
    const asset = r.asset ? `${r.asset}  ·  ` : "";
    doc.text(truncate(doc, asset + (r.title || ""), tableW - 260), M + 8, y + 13);
    // Second line under title — WO# pill + signed-by
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(90, 90, 90);
    const parts = [];
    if (r.wo) parts.push(`WO# ${r.wo}`);
    if (r.who) parts.push(`Signed by ${r.who}`);
    if (parts.length) {
      doc.text(truncate(doc, parts.join("  ·  "), tableW - 160), M + 8, y + 24);
    }
    // Right column — timestamp
    if (r.when) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      const whenW = doc.getTextWidth(r.when);
      doc.text(r.when, M + tableW - 8 - whenW, y + 18);
    }
    y += rowH;
  });
  return y + 6;
}
function drawWearTable(doc, M, y, W, rows) {
  const tableW = W - M * 2;
  const cols = [
    { key: "asset_id", label: "ASSET", w: 0.28 },
    { key: "worst_tooth", label: "TOOTH %", w: 0.18 },
    { key: "worst_lip_shroud", label: "LIP %", w: 0.18 },
    { key: "worst_wing_shroud", label: "WING %", w: 0.18 },
    { key: "last_inspected", label: "LAST", w: 0.18 },
  ];
  // header
  doc.setFillColor(30, 30, 45);
  doc.rect(M, y, tableW, 18, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  let x = M + 8;
  cols.forEach((c) => {
    doc.text(c.label, x, y + 12);
    x += c.w * tableW;
  });
  y += 18;
  // rows
  rows.forEach((r, i) => {
    doc.setFillColor(i % 2 === 0 ? 250 : 245, i % 2 === 0 ? 250 : 245, i % 2 === 0 ? 250 : 245);
    doc.rect(M, y, tableW, 20, "F");
    doc.setDrawColor(228, 228, 228);
    doc.setLineWidth(0.4);
    doc.rect(M, y, tableW, 20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(20, 20, 20);
    let xx = M + 8;
    cols.forEach((c) => {
      let v = r[c.key];
      if (c.key === "last_inspected") v = fmtWhen(v);
      else if (typeof v === "number") v = `${v}%`;
      doc.text(String(v ?? ""), xx, y + 13);
      xx += c.w * tableW;
    });
    y += 20;
  });
  return y + 6;
}
function ensureRoomSimple(doc, y, meta, needed, logo) {
  const H = doc.internal.pageSize.getHeight();
  if (y + needed < H - 60) return y;
  doc.addPage();
  drawWatermark(doc);
  drawHeader(doc, meta, logo);
  return CONTENT_TOP;
}

// =========================================================================
// BREAKDOWN PDF
// =========================================================================

export function buildBreakdownPdf(bd, rasterizedSigs = {}, logo = null) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  wrapWithSanitizer(doc);
  const brand = logo && logo.header ? logo : { header: logo, watermark: logo };
  doc.__slLogo = brand.header;
  doc.__seWatermark = brand.watermark;
  const W = doc.internal.pageSize.getWidth();

  drawWatermark(doc);
  drawHeader(doc, bd, doc.__slLogo);

  let y = CONTENT_TOP;

  // Title
  const asset = bd.asset_id || bd.asset_name || "Asset";
  doc.setTextColor(15, 15, 15);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(`ASSET BREAKDOWN REPORT — ${asset}`, M, y);
  y += 24;

  // Right badge
  drawRightBadge(doc, "ASSET BREAKDOWN REPORT", bd.site_code);

  // Status banner
  y = drawStatusBanner(doc, M, y + 4, W, breakdownStatus(bd));

  // Asset Serviceability Status — flagship engineering decision card
  // Josh (Feb 26): "Change 'Is it good to go?' to Asset Serviceability
  // Status. Update the professional engineering options to 'Released for
  // Operations' and 'Stood Down — Requires Repair'."
  y = drawServiceabilityBanner(doc, M, y + 8, W, bd);

  // Machine details
  y = drawKvTable(doc, M, y + 6, W, breakdownFacts(bd));

  // Downtime summary tile
  y = drawDowntimeTile(doc, M, y + 6, W, bd);

  // Free-text sections
  const sections = [
    { label: "FAILURE DESCRIPTION — COMPLAINT", value: bd.complaint },
    { label: "ROOT CAUSE",                       value: bd.cause },
    { label: "REPAIR ACTIONS",                   value: bd.corrective_action },
    { label: "COMMENTS",                         value: bd.comments || bd.notes },
  ];
  sections.forEach((s) => {
    if (!s.value) return;
    y = ensureRoom(doc, y, bd, 60);
    y = drawFreeText(doc, M, y + 6, W, s.label, s.value);
  });

  // Site Hazards — chip row (mirrors mobile app "Hazards on this breakdown").
  const hazards = Array.isArray(bd.hazards) ? bd.hazards.filter(Boolean) : [];
  if (hazards.length) {
    y = ensureRoom(doc, y, bd, 50);
    y = drawSectionHeader(doc, M, y + 6, `SITE HAZARDS  ·  ${hazards.length}`);
    y = drawPdfChipRow(doc, M, y + 6, W, hazards.map((h) => typeof h === "string" ? h : (h.label || h.id || "Hazard")));
  }

  // Parts Required — list of parts still on order (mirrors mobile app).
  const waitingParts = Array.isArray(bd.waiting_parts) ? bd.waiting_parts.filter(Boolean) : [];
  if (waitingParts.length) {
    y = ensureRoom(doc, y, bd, 80);
    y = drawSectionHeader(doc, M, y + 6, `PARTS REQUIRED  ·  ${waitingParts.length}`);
    y = drawPdfChipRow(doc, M, y + 6, W,
      waitingParts.map((p) => typeof p === "string" ? p
        : `${p.name || p.part_name || "Part"}${p.part_number ? " · #" + p.part_number : ""}${p.quantity ? " × " + p.quantity : ""}${p.eta ? " · ETA " + p.eta : ""}`),
      { tone: "red" });
  }

  // Follow-up flags — remaining Y/N questions (serviceability handled above).
  y = ensureRoom(doc, y, bd, 100);
  y = drawSectionHeader(doc, M, y + 6, "FOLLOW-UP FLAGS");
  y = drawBdStatusYNBlock(doc, M, y + 6, W, [
    { label: "Requires further repairs next service?", yes: !!bd.requires_next_service },
    { label: "Recurring fault?",                       yes: !!bd.is_recurring },
    { label: "Requires incoming crew work?",           yes: !!bd.requires_incoming_crew },
  ]);

  // Parts used
  if (bd.part_name || bd.part_number || bd.quantity) {
    y = ensureRoom(doc, y, bd, 60);
    y = drawSectionHeader(doc, M, y + 6, "PARTS USED");
    y = drawKvTable(doc, M, y + 4, W, [
      ["PART NAME",   bd.part_name],
      ["PART NUMBER", bd.part_number],
      ["QUANTITY",    bd.quantity],
      ["SUBCATEGORY", bd.subcategory],
    ]);
  }

  // Signature block — tradesperson + supervisor (mirrors mobile app)
  y = ensureRoom(doc, y, bd, 180, logo);
  drawSignatureBlock(doc, M, y + 10, W, {
    tradesperson_sig: bd.tradesperson_sig || bd.employee_sig,
    supervisor_sig:   bd.supervisor_sig,
    started_by_name:  bd.employee_name || bd.tradesman_name || bd.created_by_name,
    started_at:       bd.breakdown_time || bd.created_at,
    supervisor_name:  bd.supervisor_name,
    approved_at:      bd.approved_at || bd.updated_at,
  }, rasterizedSigs);

  drawFooter(doc);
  return doc;
}

export async function buildBreakdownPdfAsync(bd) {
  bd = await hydrateSiteConfig(bd);
  const [trade, sup, logo] = await Promise.all([
    rasterizeSignature(bd.tradesperson_sig || bd.employee_sig, { width: 512, bg: "#ffffff" }),
    rasterizeSignature(bd.supervisor_sig,   { width: 512, bg: "#ffffff" }),
    loadPdfBrand(bd),
  ]);
  return buildBreakdownPdf(bd, { trade, sup }, logo);
}

export async function downloadBreakdownPdf(bd) {
  const doc = await buildBreakdownPdfAsync(bd);
  // Evidence photos annex — every attached photo or markup (Josh Feb 23).
  try {
    const rawPhotos = _collectEvidencePhotos(bd);
    const photos = await _resolveEmbeddablePhotos(rawPhotos);
    if (photos.length) {
      _appendEvidencePhotoPages(doc, photos, bd, doc.__slLogo, { title: "EVIDENCE PHOTOS" });
      drawFooter(doc);
    }
  } catch { /* non-blocking */ }
  const dt = todayLocalIso(new Date(bd.approved_at || bd.updated_at || bd.breakdown_time || bd.created_at || Date.now()));
  const asset = String(bd.asset_id || bd.asset_name || "record").replace(/[^A-Za-z0-9_-]+/g, "");
  const filename = `Breakdown-${asset}-${dt}.pdf`;
  doc.save(filename);
  return filename;
}

// =========================================================================
// PPF PDF
// =========================================================================

export function buildPPFPdf(ppf, rasterizedSigs = {}, logo = null) {
  // A4 portrait. Every helper below is dimensioned to fit ONE page — even
  // for busy PPFs with 6 parts, 3 trades, and a photo. If a caller passes
  // an unusually long fault/notes we truncate rather than page-break, so
  // planners can print the whole spec front-facing without shuffling.
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  wrapWithSanitizer(doc);
  const brand = logo && logo.header ? logo : { header: logo, watermark: logo };
  doc.__slLogo = brand.header;
  doc.__seWatermark = brand.watermark;
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  drawWatermark(doc);
  drawHeader(doc, ppf, doc.__slLogo);

  let y = CONTENT_TOP;

  // Header line per Josh's spec: "PPF {asset} — {fault} — WO#{wo}".
  const asset = ppf.asset_id || ppf.asset_name || "Asset";
  const fault = (ppf.fault || ppf.description || "").trim();
  const wo = (ppf.work_order_number || "").trim();
  doc.setTextColor(15, 15, 15);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  // Title assembled in parts so we can colour the WO# in orange.
  const titleParts = [`PLANNING REQUEST · ${asset}`];
  if (fault) titleParts.push(fault.length > 80 ? fault.slice(0, 78) + "…" : fault);
  const titleMain = titleParts.join("  —  ");
  doc.text(titleMain, M, y, { maxWidth: W - M * 2 - 90 });
  if (wo) {
    doc.setTextColor(230, 105, 22);
    const woLabel = `  —  WO# ${wo}`;
    // Draw WO# after title width if it fits; else on the next line.
    doc.setFontSize(13);
    doc.text(woLabel, M + Math.min(W - M * 2 - doc.getTextWidth(woLabel) - 4, doc.getTextWidth(titleMain) + 6), y);
  }
  y += 22;

  drawRightBadge(doc, "PLANNING REQUEST · COMPLIANCE PACK", ppf.site_code);

  // Status banner — compact.
  y = drawStatusBanner(doc, M, y + 2, W, ppfStatusLabel(ppf), ppf.priority);

  // Two-column KV table so we fit more facts in less vertical space.
  y = drawKvTable(doc, M, y + 6, W, ppfFacts(ppf), { columns: 2 });

  // Fault + Notes — cap at 3 lines each so we never overflow.
  if (fault) {
    y = drawFreeText(doc, M, y + 6, W, "FAULT DESCRIPTION", clip(fault, 260));
  }
  if (ppf.notes) {
    y = drawFreeText(doc, M, y + 4, W, "NOTES", clip(ppf.notes, 200));
  }

  // Trades × Hours table — ALL trades printed (no cap). Auto-paginates
  // via ensureRoom() so busy PPFs with many trades don't lose data. This
  // is what Josh means by "simple breakdown of tradesman and hours".
  const trades = extractTrades(ppf);
  if (trades.length) {
    y = ensureRoom(doc, y, ppf, 40);
    y = drawSectionHeader(doc, M, y + 6, "TRADES NEEDED · HOURS PER TRADE");
    y = drawTradesTable(doc, M, y + 4, W, trades);
  }

  // Parts table — ALL parts printed (no cap). Auto-paginates so every
  // part number, quantity and store location prints even for large
  // shopping lists.
  const parts = Array.isArray(ppf.parts_needed) ? ppf.parts_needed : [];
  if (parts.length) {
    y = ensureRoom(doc, y, ppf, 40);
    y = drawSectionHeader(doc, M, y + 6, `PARTS NEEDED  ·  ${parts.length}`);
    y = drawPartsTable(doc, M, y + 4, W, parts);
  }

  // Tooling / other requirements — inline chips row (max 60 chars combined).
  const tooling = toArr(ppf.tooling_required);
  const other = toArr(ppf.other_requirements);
  if (tooling.length || other.length) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(60, 60, 60);
    if (tooling.length) {
      doc.text("TOOLING", M, y + 12);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(30, 30, 30);
      doc.text(clip(tooling.join(" · "), 110), M + 60, y + 12, { maxWidth: W - M * 2 - 60 });
      y += 14;
    }
    if (other.length) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(60, 60, 60);
      doc.text("OTHER", M, y + 12);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(30, 30, 30);
      doc.text(clip(other.join(" · "), 110), M + 60, y + 12, { maxWidth: W - M * 2 - 60 });
      y += 14;
    }
  }

  // Send-back reason — inline compact.
  if (ppf.sent_back_reason) {
    y = ensureRoom(doc, y, ppf, 60);
    y = drawFreeText(doc, M, y + 4, W, "SENT BACK", clip(ppf.sent_back_reason, 200), [220, 40, 40]);
  }

  // Parts book photos are appended as their own FULL-PAGE annex by
  // downloadPPFPdf (Josh Feb 23: "with PPF the image attached is a full
  // page please for parts"). We no longer inline a thumbnail here.

  // Signatures — auto-paginate if we've overflowed onto a new page from
  // parts / trades. Fixed y just above the footer on whichever page we
  // land on.
  y = ensureRoom(doc, y, ppf, 120);
  const sigY = Math.max(y + 20, H - 120);
  drawSignatureBlock(doc, M, sigY, W, {
    tradesperson_sig: ppf.tradesperson_sig || ppf.author_sig,
    supervisor_sig:   ppf.supervisor_sig || ppf.completed_by_sig,
    started_by_name:  ppf.created_by_name || ppf.author,
    started_at:       ppf.submitted_at || ppf.created_at,
    supervisor_name:  ppf.completed_by_name || ppf.supervisor_name || ppf.approved_by_name,
    approved_at:      ppf.completed_at || ppf.approved_at || ppf.updated_at,
  }, rasterizedSigs, { leftLabel: "AUTHOR", rightLabel: "SUPERVISOR" });

  drawFooter(doc);
  return doc;
}

// PPF PDF helpers ---------------------------------------------------------

function extractTrades(ppf) {
  if (Array.isArray(ppf.trades_needed) && ppf.trades_needed.length) {
    return ppf.trades_needed
      .map((t) => ({ trade: t.trade || t.role || "", qty: t.qty ?? null, hours: t.hours != null && t.hours !== "" ? Number(t.hours) : null }))
      .filter((t) => t.trade);
  }
  const legacy = Array.isArray(ppf.human_resources_list)
    ? ppf.human_resources_list
    : (typeof ppf.human_resources === "string" && ppf.human_resources
      ? ppf.human_resources.split(",").map((x) => x.trim()).filter(Boolean)
      : []);
  return legacy.map((raw) => {
    // Newer backends store each trade as an object
    // { trade, qty, hours, notes } — use those fields directly instead
    // of stringifying (which produced "[object Object]" in the PDF).
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      return {
        trade: String(raw.trade || raw.role || raw.name || "").trim(),
        qty: raw.qty ?? null,
        hours: raw.hours != null && raw.hours !== "" ? Number(raw.hours) : null,
      };
    }
    const m = String(raw).match(/^(.+?)[\u00B7·:\-]+\s*([\d.]+)\s*(h|hr|hrs)?$/i);
    if (m) return { trade: m[1].trim(), qty: null, hours: Number(m[2]) };
    return { trade: String(raw).trim(), qty: null, hours: null };
  }).filter((t) => t.trade);
}

function drawTradesTable(doc, M, y, W, rows) {
  const tableW = W - M * 2;
  const rowH = 18;
  const colQtyX = M + tableW - 170;
  const colHrsX = M + tableW - 90;
  // Header
  doc.setFillColor(238, 238, 238);
  doc.rect(M, y, tableW, rowH, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text("TRADE", M + 8, y + 12);
  doc.text("QTY", colQtyX, y + 12);
  doc.text("EST. HOURS", colHrsX, y + 12);
  y += rowH;
  // Body
  let total = 0;
  rows.forEach((t, i) => {
    doc.setFillColor(i % 2 ? 248 : 253, i % 2 ? 248 : 253, i % 2 ? 248 : 253);
    doc.rect(M, y, tableW, rowH, "F");
    doc.setDrawColor(228, 228, 228);
    doc.setLineWidth(0.3);
    doc.line(M, y + rowH, M + tableW, y + rowH);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(20, 20, 20);
    doc.text(String(t.trade || "—"), M + 8, y + 12, { maxWidth: tableW - 190 });
    doc.setFont("helvetica", "normal");
    const qStr = t.qty != null ? String(t.qty) : "—";
    doc.text(qStr, colQtyX, y + 12);
    const hStr = Number.isFinite(t.hours) ? `${t.hours} hrs` : "—";
    if (Number.isFinite(t.hours)) total += Number(t.hours) * (Number(t.qty) || 1);
    doc.text(hStr, M + tableW - 8 - doc.getTextWidth(hStr), y + 12);
    y += rowH;
  });
  // Total row
  if (total > 0) {
    doc.setFillColor(255, 244, 231);
    doc.rect(M, y, tableW, rowH, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(230, 105, 22);
    doc.text("TOTAL", M + 8, y + 12);
    const tStr = `${total} hrs`;
    doc.text(tStr, M + tableW - 8 - doc.getTextWidth(tStr), y + 12);
    y += rowH;
  }
  return y;
}

function drawPartsTable(doc, M, y, W, rows) {
  const tableW = W - M * 2;
  const rowH = 16;
  const cols = {
    part: 100,
    name: tableW - 100 - 60 - 100 - 8,
    qty: 60,
    store: 100,
  };
  // Header
  doc.setFillColor(238, 238, 238);
  doc.rect(M, y, tableW, rowH, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(60, 60, 60);
  let x = M + 8;
  doc.text("PART #", x, y + 11); x += cols.part;
  doc.text("NAME / DESCRIPTION", x, y + 11); x += cols.name;
  doc.text("QTY", x, y + 11); x += cols.qty;
  doc.text("STORE", x, y + 11);
  y += rowH;
  rows.forEach((p, i) => {
    doc.setFillColor(i % 2 ? 250 : 254, i % 2 ? 250 : 254, i % 2 ? 250 : 254);
    doc.rect(M, y, tableW, rowH, "F");
    doc.setDrawColor(232, 232, 232);
    doc.setLineWidth(0.3);
    doc.line(M, y + rowH, M + tableW, y + rowH);
    x = M + 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(20, 20, 20);
    doc.text(clip(String(p.part_number || "—"), 15), x, y + 11); x += cols.part;
    doc.setFont("helvetica", "normal");
    doc.text(clip(String(p.part_name || p.description || "—"), 40), x, y + 11, { maxWidth: cols.name - 4 }); x += cols.name;
    doc.text(String(p.quantity ?? "—"), x, y + 11); x += cols.qty;
    doc.text(clip(String(p.store_location || "—"), 14), x, y + 11);
    y += rowH;
  });
  return y;
}

function drawPartsPhoto(doc, M, y, W, maxY, url) {
  try {
    const availH = Math.max(80, maxY - y);
    const targetH = Math.min(180, availH);
    const targetW = Math.min(W - M * 2, targetH * 1.4);
    // Draw a subtle frame + label first — jsPDF may fail to embed a data
    // URL of an unsupported type, so we always render the "PARTS PHOTO"
    // caption regardless of the image outcome.
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.rect(M, y, targetW, targetH);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(60, 60, 60);
    doc.text("PARTS PHOTO", M, y - 4);
    // Try to embed the image. jsPDF's addImage throws on unknown formats,
    // so we swallow to preserve the single-page guarantee.
    doc.addImage(url, "JPEG", M + 1, y + 1, targetW - 2, targetH - 2, undefined, "FAST");
  } catch { /* photo missing / unsupported — the frame stays as a placeholder */ }
}

function firstImageUrl(list) {
  if (!Array.isArray(list) || !list.length) return null;
  for (const p of list) {
    if (!p) continue;
    if (typeof p === "string") return p;
    const url = p.url || p.data || p.photo_url || p.href;
    if (url) return url;
  }
  return null;
}

function toArr(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === "string") return v.split(",").map((x) => x.trim()).filter(Boolean);
  return [];
}

function clip(s, n) {
  const str = String(s || "");
  return str.length > n ? str.slice(0, n - 1) + "…" : str;
}

// End PPF PDF helpers -----------------------------------------------------

export async function buildPPFPdfAsync(ppf) {
  ppf = await hydrateSiteConfig(ppf);
  const [trade, sup, logo] = await Promise.all([
    rasterizeSignature(ppf.tradesperson_sig || ppf.author_sig,          { width: 512, bg: "#ffffff" }),
    rasterizeSignature(ppf.supervisor_sig  || ppf.completed_by_sig,     { width: 512, bg: "#ffffff" }),
    loadPdfBrand(ppf),
  ]);
  return buildPPFPdf(ppf, { trade, sup }, logo);
}

export async function downloadPPFPdf(ppf) {
  const doc = await buildPPFPdfAsync(ppf);
  // Parts book photos → one FULL PAGE per photo so planners can read
  // every part number and diagram (Josh Feb 23: "the image attached is a
  // full page please for parts").
  try {
    const partsPhotos = await _resolveEmbeddablePhotos(ppf.parts_book_photos || []);
    if (partsPhotos.length) {
      _appendEvidencePhotoPages(doc, partsPhotos, ppf, doc.__slLogo, {
        title: "PARTS BOOK PHOTO",
        fullPagePerPhoto: true,
      });
    }
    // Any other attached photos (site shots, damage snaps) → grouped
    // evidence pages.
    const otherRaw = [
      ...(Array.isArray(ppf.photos) ? ppf.photos : []),
      ...(Array.isArray(ppf.markups) ? ppf.markups : []),
    ];
    const otherPhotos = await _resolveEmbeddablePhotos(otherRaw);
    if (otherPhotos.length) {
      _appendEvidencePhotoPages(doc, otherPhotos, ppf, doc.__slLogo, { title: "EVIDENCE PHOTOS" });
    }
    if (partsPhotos.length || otherPhotos.length) drawFooter(doc);
  } catch { /* non-blocking */ }
  const dt = todayLocalIso(new Date(ppf.completed_at || ppf.approved_at || ppf.submitted_at || ppf.created_at || Date.now()));
  const asset = String(ppf.asset_id || ppf.asset_name || "record").replace(/[^A-Za-z0-9_-]+/g, "");
  const filename = `PPF-${asset}-${dt}.pdf`;
  doc.save(filename);
  return filename;
}

// =========================================================================
// SHARED DRAW HELPERS FOR BREAKDOWN / PPF
// =========================================================================

function drawRightBadge(doc, badge, site) {
  const W = doc.internal.pageSize.getWidth();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(badge, W - M - doc.getTextWidth(badge), CONTENT_TOP);
  if (site) {
    doc.setFont("helvetica", "normal");
    doc.text(String(site).toUpperCase(), W - M - doc.getTextWidth(String(site).toUpperCase()), CONTENT_TOP + 14);
  }
  const stamp = `Generated ${new Date().toLocaleString()}`;
  doc.setFontSize(8.5);
  doc.text(stamp, W - M - doc.getTextWidth(stamp), CONTENT_TOP + 28);
}

function drawStatusBanner(doc, M, y, W, statusLabel, priority) {
  const label = String(statusLabel || "").toUpperCase();
  const tone = statusBannerTone(label);
  const h = 32;
  doc.setFillColor(...tone.bg);
  doc.roundedRect(M, y, W - M * 2, h, 6, 6, "F");
  doc.setTextColor(...tone.text);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(label, M + 12, y + 20);
  if (priority) {
    const pText = `PRIORITY · ${String(priority).toUpperCase()}`;
    doc.setFontSize(9);
    doc.text(pText, W - M - doc.getTextWidth(pText) - 12, y + 20);
  }
  return y + h;
}

function statusBannerTone(label) {
  if (/APPROVED|COMPLETE|CLOSED|DONE|SIGNED|RESOLVED/.test(label))
    return { bg: [220, 245, 224], text: [24, 118, 40] };
  if (/PENDING|SUBMIT|AWAIT|REVIEW/.test(label))
    return { bg: [255, 236, 213], text: [178, 92, 12] };
  if (/DRAFT/.test(label))
    return { bg: [232, 232, 232], text: [90, 90, 90] };
  if (/OPEN|DOWN|CRITICAL/.test(label))
    return { bg: [253, 224, 224], text: [178, 34, 34] };
  return { bg: [232, 232, 232], text: [90, 90, 90] };
}

function drawKvTable(doc, M, y, W, rows, opts = {}) {
  const filtered = (rows || []).filter((r) => r && r[1] != null && r[1] !== "");
  if (!filtered.length) return y;
  const columns = Math.max(1, Math.min(2, opts.columns || 1));
  const rowH = columns === 2 ? 18 : 22;
  const labelColW = columns === 2 ? 100 : 140;
  const tableW = W - M * 2;
  const colW = tableW / columns;
  const rowCount = Math.ceil(filtered.length / columns);
  filtered.forEach((r, i) => {
    const col = i % columns;
    const rowIdx = Math.floor(i / columns);
    const cellX = M + col * colW;
    const rowY = y + rowIdx * rowH;
    doc.setFillColor(rowIdx % 2 === 0 ? 250 : 245, rowIdx % 2 === 0 ? 250 : 245, rowIdx % 2 === 0 ? 250 : 245);
    doc.rect(cellX, rowY, colW, rowH, "F");
    doc.setDrawColor(228, 228, 228);
    doc.setLineWidth(0.4);
    doc.rect(cellX, rowY, colW, rowH);
    doc.setTextColor(90, 90, 90);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(columns === 2 ? 7.5 : 8);
    doc.text(String(r[0]).toUpperCase(), cellX + 8, rowY + (columns === 2 ? 12 : 14));
    doc.setTextColor(20, 20, 20);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(columns === 2 ? 9 : 10);
    doc.text(truncate(doc, String(r[1]), colW - labelColW - 12), cellX + labelColW, rowY + (columns === 2 ? 12 : 14));
  });
  return y + rowCount * rowH + 6;
}

function drawSectionHeader(doc, M, y, label) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(245, 130, 32);
  doc.text(label, M, y + 12);
  doc.setDrawColor(245, 130, 32);
  doc.setLineWidth(1.4);
  const underlineW = Math.min(60, doc.getTextWidth(label));
  doc.line(M, y + 16, M + underlineW, y + 16);
  doc.setLineWidth(0.4);
  return y + 20;
}

/**

/**
 * drawBdStatusYNBlock — renders the mobile-app "STATUS" block: a stack
 * of rounded rows, each with a question on the left and a YES / NO
 * pill pair on the right. The chosen answer is highlighted in
 * safety-orange, the other muted grey. Josh Feb 23 spec — replaces the
 * old chip-row of lifecycle flags.
 */
/**
 * drawServiceabilityBanner — flagship "engineering decision" card at the
 * top of every breakdown PDF. Green = released for operations, orange =
 * stood down. Josh (Feb 26): "Make the toggle look like a high-end
 * engineering decision, not a basic yes/no checkbox."
 */
function drawServiceabilityBanner(doc, M, y, W, bd) {
  const released = !!(bd.back_in_service_time || bd.is_finalised);
  const label = released ? "RELEASED FOR OPERATIONS" : "STOOD DOWN — REQUIRES REPAIR";
  const authorisedBy = bd.approved_by_name || bd.locked_by_name || bd.supervisor_name || bd.completed_by_name || "—";
  const tableW = W - M * 2;
  const bandH = 22;
  const bodyH = 56;
  const totalH = bandH + bodyH;
  const [fillR, fillG, fillB] = released ? [232, 247, 236] : [254, 244, 238];
  const [strokeR, strokeG, strokeB] = released ? [76, 175, 80] : [249, 115, 22];
  const [chipR, chipG, chipB] = released ? [46, 125, 50] : [194, 65, 12];
  // Card outer
  doc.setDrawColor(strokeR, strokeG, strokeB);
  doc.setLineWidth(1.1);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(M, y, tableW, totalH, 8, 8, "FD");
  // Top band with the category
  doc.setFillColor(fillR, fillG, fillB);
  doc.rect(M + 1, y + 1, tableW - 2, bandH - 1, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(chipR, chipG, chipB);
  doc.text("ASSET SERVICEABILITY STATUS", M + 12, y + bandH / 2 + 3);
  const rightLabel = "ENGINEERING DECISION";
  const rw = doc.getTextWidth(rightLabel);
  doc.text(rightLabel, M + tableW - 12 - rw, y + bandH / 2 + 3);
  // Body — icon disc + label + authorisation
  const iy = y + bandH + 8;
  const discX = M + 12, discSize = 32;
  doc.setFillColor(fillR, fillG, fillB);
  doc.setDrawColor(strokeR, strokeG, strokeB);
  doc.roundedRect(discX, iy, discSize, discSize, 6, 6, "FD");
  // Simple check / warning glyph
  doc.setDrawColor(chipR, chipG, chipB);
  doc.setLineWidth(2.4);
  if (released) {
    doc.line(discX + 8,  iy + 17, discX + 14, iy + 22);
    doc.line(discX + 14, iy + 22, discX + 25, iy + 10);
  } else {
    // Triangle warning
    doc.line(discX + 16, iy + 6,  discX + 6,  iy + 24);
    doc.line(discX + 16, iy + 6,  discX + 26, iy + 24);
    doc.line(discX + 6,  iy + 24, discX + 26, iy + 24);
    doc.setLineWidth(1.6);
    doc.line(discX + 16, iy + 12, discX + 16, iy + 18);
    doc.setFillColor(chipR, chipG, chipB);
    doc.circle(discX + 16, iy + 21, 0.9, "F");
  }
  // Text block
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(15, 15, 15);
  doc.text(label, discX + discSize + 12, iy + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(85, 85, 85);
  doc.text(`Authorised By: ${authorisedBy}`, discX + discSize + 12, iy + 28);
  return y + totalH;
}

function drawBdStatusYNBlock(doc, M, y, W, questions) {
  if (!questions || !questions.length) return y;
  const tableW = W - M * 2;
  const rowH = 28;
  const gap = 6;
  const pillW = 44;
  const pillH = 18;
  questions.forEach((q, i) => {
    // Row background — subtle stripe alternating for readability.
    doc.setFillColor(i % 2 ? 250 : 253, i % 2 ? 250 : 253, i % 2 ? 250 : 253);
    doc.setDrawColor(228, 228, 228);
    doc.setLineWidth(0.4);
    doc.roundedRect(M, y, tableW, rowH, 5, 5, "FD");
    // Question label
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(25, 25, 25);
    doc.text(String(q.label || ""), M + 10, y + rowH / 2 + 3, { maxWidth: tableW - (pillW * 2 + gap) - 24 });
    // Y/N pills anchored to the right
    const rightX = M + tableW - 10 - (pillW * 2 + gap);
    const py = y + (rowH - pillH) / 2;
    const drawPill = (px, text, active) => {
      if (active) {
        doc.setFillColor(249, 115, 22);
        doc.setDrawColor(249, 115, 22);
        doc.setTextColor(255, 255, 255);
      } else {
        doc.setFillColor(241, 241, 241);
        doc.setDrawColor(229, 229, 229);
        doc.setTextColor(138, 138, 138);
      }
      doc.roundedRect(px, py, pillW, pillH, 4, 4, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text(text, px + (pillW - doc.getTextWidth(text)) / 2, py + pillH / 2 + 3.2);
    };
    drawPill(rightX,                    "YES", !!q.yes);
    drawPill(rightX + pillW + gap,      "NO",  !q.yes);
    y += rowH + 4;
  });
  return y;
}

/**
 * drawPdfChipRow — renders a wrapped row of pill-style chips (site
 * hazards, waiting parts, lifecycle flags). Auto-wraps to a new line
 * when a chip would exceed the page width. Mirrors the coloured
 * `ChipRow` component used in the portal doc view so PDF ↔ screen stay
 * visually 1:1.
 *
 * Palette:
 *   orange (default) → informational
 *   red              → blocking (e.g. waiting on parts)
 *   amber            → attention (e.g. RECURRING / LOCKED)
 */
function drawPdfChipRow(doc, M, y, W, items, opts = {}) {
  if (!items || !items.length) return y;
  const tone = opts.tone || "orange";
  const palette = {
    orange: { bg: [255, 243, 224], fg: [199, 106, 0], bd: [255, 204, 128] },
    red:    { bg: [253, 224, 224], fg: [178, 34, 34], bd: [244, 143, 143] },
    amber:  { bg: [255, 236, 179], fg: [148, 96, 8],  bd: [255, 213, 79]  },
  };
  const P = palette[tone] || palette.orange;
  const tableW = W - M * 2;
  const chipH = 18;
  const gap = 6;
  const padX = 8;

  let x = M;
  let lineY = y;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  items.forEach((label) => {
    const text = String(label || "").trim();
    if (!text) return;
    const textW = doc.getTextWidth(text);
    const chipW = textW + padX * 2;
    if (x + chipW > M + tableW) {
      // wrap to next line
      x = M;
      lineY += chipH + 4;
    }
    doc.setDrawColor(...P.bd);
    doc.setFillColor(...P.bg);
    doc.roundedRect(x, lineY, chipW, chipH, 4, 4, "FD");
    doc.setTextColor(...P.fg);
    doc.text(text, x + padX, lineY + 12);
    x += chipW + gap;
  });
  return lineY + chipH + 4;
}


function drawFreeText(doc, M, y, W, label, text, color) {
  y = drawSectionHeader(doc, M, y, label);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...(color || [30, 30, 30]));
  const lines = doc.splitTextToSize(String(text), W - M * 2);
  lines.forEach((ln) => { doc.text(ln, M, y + 12); y += 12; });
  return y + 4;
}

function drawBullets(doc, M, y, W, items) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(30, 30, 30);
  const list = Array.isArray(items) ? items : [items];
  list.forEach((it) => {
    const label = typeof it === "string"
      ? it
      : (it?.name || it?.label || it?.description || it?.text || JSON.stringify(it));
    doc.setTextColor(245, 130, 32);
    doc.text("\u2022", M + 4, y + 12);
    doc.setTextColor(30, 30, 30);
    const lines = doc.splitTextToSize(String(label), W - M * 2 - 20);
    lines.forEach((ln, i) => doc.text(ln, M + 16, y + 12 + i * 12));
    y += Math.max(14, lines.length * 12);
  });
  return y + 4;
}

function drawDowntimeTile(doc, M, y, W, bd) {
  const down = bd.breakdown_time || bd.started_at;
  const up = bd.back_in_service_time || bd.finished_at;
  const duration = bd.downtime_duration || computeDuration(down, up);
  if (!down && !up && !duration) return y;
  const h = 46;
  const gap = 8;
  const tiles = [
    { label: "DOWN AT",         value: fmt(down) || "—" },
    { label: "BACK IN SERVICE", value: fmt(up) || "—" },
    { label: "DOWNTIME",        value: duration || "—", accent: true },
  ];
  const cellW = (W - M * 2 - gap * (tiles.length - 1)) / tiles.length;
  tiles.forEach((t, i) => {
    const x = M + i * (cellW + gap);
    doc.setFillColor(...(t.accent ? [255, 236, 213] : [246, 246, 246]));
    doc.roundedRect(x, y, cellW, h, 8, 8, "F");
    doc.setTextColor(...(t.accent ? [178, 92, 12] : [110, 110, 110]));
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.text(t.label, x + 10, y + 14);
    doc.setTextColor(...(t.accent ? [178, 92, 12] : [20, 20, 20]));
    doc.setFontSize(11);
    doc.text(truncate(doc, String(t.value), cellW - 20), x + 10, y + 34);
  });
  return y + h + 6;
}

function computeDuration(a, b) {
  if (!a || !b) return null;
  try {
    const d1 = new Date(a);
    const d2 = new Date(b);
    if (isNaN(d1) || isNaN(d2) || d2 <= d1) return null;
    const mins = Math.round((d2 - d1) / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  } catch { return null; }
}

function breakdownStatus(bd) {
  if (bd.approved_at || String(bd.status || "").toLowerCase().includes("approved")) return "APPROVED";
  if (bd.back_in_service_time || bd.finished_at) return "CLOSED";
  return "OPEN · DOWN";
}
function ppfStatusLabel(ppf) {
  const s = String(ppf?.status || ppf?.stage || "").toLowerCase();
  if (/approved|signed|complete|closed|resolved|done/.test(s)) return "APPROVED";
  if (/pending|submit|await|review/.test(s)) return "PENDING SIGNING";
  return "DRAFT";
}
function breakdownFacts(bd) {
  return [
    ["FLEET UNIT",     bd.asset_id || bd.asset_name],
    ["SERIAL NUMBER",  bd.serial_number || bd.asset_serial_number],
    ["SITE",           bd.site_code],
    ["EMPLOYEE",       bd.employee_name],
    ["SUPERVISOR",     bd.supervisor_name],
    ["CREW",           bd.crew_name],
    ["SMU (HRS)",      bd.smu],
    ["LOCATION",       bd.location],
    ["SUBCATEGORY",    cap(bd.subcategory)],
    ["PRIORITY",       cap(bd.priority || bd.severity)],
    ["STATUS",         String(bd.status || (bd.is_finalised ? "finalised" : "open")).toUpperCase()],
    ["WORK ORDER #",   bd.work_order_number],
    ["APPROVED AT",    fmt(bd.approved_at)],
    ["APPROVED BY",    bd.approved_by_name || bd.locked_by_name],
  ];
}
function ppfFacts(ppf) {
  return [
    ["FLEET UNIT",         ppf.asset_id || ppf.asset_name],
    ["SERIAL NUMBER",      ppf.serial_number || ppf.asset_serial_number],
    ["SITE",               ppf.site_code],
    ["AUTHOR",             ppf.created_by_name || ppf.author],
    ["SUPERVISOR",         ppf.completed_by_name || ppf.supervisor_name || ppf.approved_by_name],
    ["SMU (HRS)",          ppf.smu],
    ["PRIORITY",           cap(ppf.priority)],
    ["STATUS",             ppfStatusLabel(ppf)],
    ["WORK ORDER #",       ppf.work_order_number],
    ["ESTIMATED HOURS",    ppf.estimated_hours],
    ["PEOPLE",             ppf.num_people],
    ["SPECIALIST SKILL",   ppf.specialist_skill],
    ["TARGET COMPLETION",  fmt(ppf.target_completion)],
    ["SUBMITTED",          fmt(ppf.submitted_at)],
    ["COMPLETED",          fmt(ppf.completed_at)],
    ["APPROVED",           fmt(ppf.approved_at)],
  ];
}

// =========================================================================
// RENDERERS
// =========================================================================

function drawHeader(doc, insp, logo = null) {
  const W = doc.internal.pageSize.getWidth();

  // Real Pit Link wordmark asset — the same PNG the app's PDF embeds.
  // Falls back to a synthetic badge if the asset failed to load.
  // (Feb 27: doubled per Josh — logo now a prominent 108pt tall centrepiece.)
  if (logo && logo.dataUrl && logo.w && logo.h) {
    const targetH = 108;
    const targetW = Math.min(520, (logo.w / logo.h) * targetH);
    try {
      doc.addImage(logo.dataUrl, "PNG", M, 16, targetW, targetH);
    } catch {
      drawFallbackBadge(doc);
    }
  } else {
    drawFallbackBadge(doc);
  }

  // Thin rule below the header
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.6);
  doc.line(M, HEADER_H + 4, W - M, HEADER_H + 4);
}

function drawFallbackBadge(doc) {
  // Kept as a graceful fallback if the PNG asset ever fails to load.
  doc.setFillColor(15, 15, 15);
  doc.circle(M + 14, 40, 16, "F");
  doc.setTextColor(245, 130, 32);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("S", M + 14 - doc.getTextWidth("S") / 2, 46);
  doc.setTextColor(20, 20, 20);
  doc.setFontSize(11);
  doc.text("Pit Link", M + 38, 40);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(130, 130, 130);
  doc.text("MAINTENANCE HUB", M + 38, 52);
}

function drawWatermark(doc) {
  // Subtle PIT LINK brand watermark — the SE wordmark, centred on the
  // page at ~6% opacity, on every content page. Restored per Josh's Feb 28
  // "MANDATORY WATERMARK" directive (a single faded logo, not the tiled
  // diagonal text that lived here before). Site-specific PDF header logos
  // do NOT displace this — the Pit Link mark still stamps every page as
  // the platform-of-record.
  //
  // Prefer the dedicated `__seWatermark` (always the Pit Link wordmark)
  // over `__slLogo` (which may be a customer's site logo).
  const logo = doc.__seWatermark || doc.__slLogo;
  if (!logo || !logo.dataUrl || !logo.w || !logo.h) return;
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  // Draw at ~55% of the page width, centred.
  const targetW = W * 0.55;
  const targetH = (logo.h / logo.w) * targetW;
  const x = (W - targetW) / 2;
  const y = (H - targetH) / 2;
  if (doc.saveGraphicsState) doc.saveGraphicsState();
  if (doc.setGState && doc.GState) {
    try { doc.setGState(new doc.GState({ opacity: 0.06 })); } catch { /* older jspdf */ }
  }
  try {
    doc.addImage(logo.dataUrl, "PNG", x, y, targetW, targetH, undefined, "FAST");
  } catch { /* ignore render failures — watermark is best-effort */ }
  if (doc.restoreGraphicsState) doc.restoreGraphicsState();
}

function drawFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    // Clear any previous footer text so re-drawing after appending
    // pages (evidence photos) leaves a single, correctly-numbered line.
    doc.setFillColor(255, 255, 255);
    doc.rect(0, H - 50, W, 50, "F");
    doc.setDrawColor(220, 220, 220);
    doc.line(M, H - 44, W - M, H - 44);
    doc.setTextColor(130, 130, 130);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text("Pit Link · Maintenance Hub", M, H - 28);
    const pgLabel = `Page ${i} of ${pageCount}`;
    doc.text(pgLabel, W - M - doc.getTextWidth(pgLabel), H - 28);
  }
}

function drawMachineDetails(doc, M, y, W, insp) {
  const rows = [];
  const push = (k, v) => {
    if (v === undefined || v === null || v === "") return;
    rows.push([k, String(v)]);
  };
  const meta = insp.asset_meta || {};
  // Locate the SMU measurement item's value so the PDF reflects hours entered
  // during the shift (not just what's set on approval).
  const state = insp.items_state || {};
  const smuFromItem = (() => {
    for (const [id, s] of Object.entries(state)) {
      if (!s) continue;
      if (/^smu[-_]?\d?$/i.test(id) && (s.value ?? s.value_str)) return s.value ?? s.value_str;
    }
    return null;
  })();
  // Compliance PDF headline — the three fields auditors ask for first.
  // Rendered as explicit rows even when empty so the audit trail always
  // documents what was captured (or NOT captured, in the case of missing WO).
  push("FLEET UNIT", insp.asset_id || "—");
  push("SERIAL NUMBER", meta.serial_number || insp.serial_number || insp.asset_serial_number);
  push("WORK ORDER #", insp.work_order_number || "—");
  push("DATE", fmt(insp.approved_at || insp.updated_at || insp.started_at || insp.created_at) || "—");
  push("MACHINE MODEL", meta.machine_model || insp.machine_model);
  push("ASSET TYPE", meta.asset_type || insp.machine_type);
  push("CATEGORY", meta.category);
  push("ENGINE CONFIG", meta.engine_config || insp.engine_config);
  push("SITE", insp.site_code);
  push("SHIFT", cap(insp.shift));
  push("SMU (hrs)", insp.smu || smuFromItem || insp.items_state?.["dz-parts-smu"]?.value || insp.items_state?.smu?.value);
  push("STARTED BY", `${insp.started_by_name || insp.started_by || ""}${insp.started_at ? " · " + fmt(insp.started_at) : ""}`.trim());
  push("CREW", insp.assigned_crew_name);
  push("WORK AREA", cap(insp.assigned_work_area));
  push("SUPERVISOR", insp.supervisor_name);
  push("STATUS", (insp.status || "").toUpperCase());
  if (insp.approved_at) push("APPROVED", fmt(insp.approved_at));

  if (!rows.length) return y;

  // Single-column table with alternating rows (matches the app PDF exactly).
  const rowH = 22;
  const labelColW = 130;
  const tableW = W - M * 2;
  rows.forEach((r, i) => {
    const rowY = y + i * rowH;
    // Row background
    doc.setFillColor(i % 2 === 0 ? 250 : 245, i % 2 === 0 ? 250 : 245, i % 2 === 0 ? 250 : 245);
    doc.rect(M, rowY, tableW, rowH, "F");
    // Border
    doc.setDrawColor(228, 228, 228);
    doc.setLineWidth(0.4);
    doc.rect(M, rowY, tableW, rowH);
    // Label
    doc.setTextColor(90, 90, 90);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(r[0], M + 10, rowY + 14);
    // Value
    doc.setTextColor(20, 20, 20);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(truncate(doc, r[1], tableW - labelColW - 20), M + labelColW, rowY + 14);
  });
  return y + rows.length * rowH + 8;
}

function drawStatRow(doc, M, y, W, insp) {
  // The API's `item_summary` already rolls up NEW / 25 / 50 / 75 / C-O for us.
  // Only render the wear tiers when this inspection has wear_pct items — the
  // app's PDF does the same.
  const sum = insp.item_summary || {};
  const newCount = num(sum.new);
  const w25 = num(sum["25"]);
  const w50 = num(sum["50"]);
  const w75 = num(sum["75"]);
  const wco = num(sum.co || sum.wear_change_out || 0);
  const includeWear = newCount + w25 + w50 + w75 + wco > 0;

  const cells = [
    { key: "ok",    value: num(sum.ok) },
    { key: "issue", value: num(sum.issue) },
    { key: "na",    value: num(sum.na) },
    { key: "ppf",   value: num(sum.ppf || sum.priority || 0) },
  ];
  if (includeWear) {
    cells.push({ key: "new",  value: newCount, label: "NEW",     bg: [200, 240, 210], text: [24, 118, 40] });
    cells.push({ key: "w25",  value: w25,      label: "25%",     bg: [220, 245, 224], text: [24, 118, 40] });
    cells.push({ key: "w50",  value: w50,      label: "50%",     bg: [255, 246, 200], text: [178, 132, 12] });
    cells.push({ key: "wear75", value: w75 });
    cells.push({ key: "co",   value: wco });
  }

  const gap = 8;
  const cellW = (W - M * 2 - gap * (cells.length - 1)) / cells.length;
  const h = 46;
  cells.forEach((c, i) => {
    const x = M + i * (cellW + gap);
    const st = c.bg ? { bg: c.bg, text: c.text, label: c.label } : (STATUS[c.key] || STATUS.na);
    doc.setFillColor(...st.bg);
    doc.roundedRect(x, y, cellW, h, 8, 8, "F");
    doc.setTextColor(...st.text);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    const vw = doc.getTextWidth(String(c.value));
    doc.text(String(c.value), x + (cellW - vw) / 2, y + 24);
    doc.setFontSize(7.5);
    const lw = doc.getTextWidth(st.label);
    doc.text(st.label, x + (cellW - lw) / 2, y + 38);
  });
  return y + h + 12;
}

function drawSection(doc, M, y, W, section, itemsState, insp) {
  y = ensureRoom(doc, y, insp, 60);

  // Section header — orange caps like the app PDF, with a short orange underline.
  const label = String(section.name || "SECTION").toUpperCase();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(245, 130, 32);
  doc.text(label, M, y);
  doc.setDrawColor(245, 130, 32);
  doc.setLineWidth(1.4);
  const underlineW = Math.min(60, doc.getTextWidth(label));
  doc.line(M, y + 4, M + underlineW, y + 4);
  doc.setLineWidth(0.4);
  y += 16;

  // Section intro
  if (section.intro) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9.5);
    doc.setTextColor(90, 90, 90);
    const lines = doc.splitTextToSize(String(section.intro), W - M * 2);
    lines.forEach((ln) => { doc.text(ln, M, y); y += 12; });
    doc.setFont("helvetica", "normal");
    y += 4;
  }

  // Column widths
  const cItemW = (W - M * 2) * 0.52;
  const cStatusW = (W - M * 2) * 0.16;
  const cWhoW = (W - M * 2) * 0.16;
  const cWhenW = (W - M * 2) - cItemW - cStatusW - cWhoW;

  // Table header — dark navy band with white caps, matching the app.
  doc.setFillColor(24, 32, 46);
  doc.rect(M, y, W - M * 2, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text("ITEM",         M + 10,                                    y + 14);
  doc.text("STATUS",       M + cItemW + 10,                           y + 14);
  doc.text("COMPLETED BY", M + cItemW + cStatusW + 10,                y + 14);
  doc.text("WHEN",         M + cItemW + cStatusW + cWhoW + 10,        y + 14);
  y += 22;

  // Body rows — reserve enough space for the ACTUAL rendered height,
  // not a hard-coded 40pt. Long labels wrap over multiple lines and
  // pin_matrix items expand into a 4-row grid + summary + track id.
  let rowIdx = 0;
  (section.items || []).forEach((item) => {
    const st = itemsState[item.id] || {};
    const isPin = (item.kind || item.type) === "pin_matrix";
    // Compute the height of the row + any inline extras (comment / action /
    // measurement) so ensureRoom can page-break BEFORE we start drawing.
    const labelH = wrapHeight(doc, item.label || item.name || "—", cItemW - 16, 9.5);
    let neededH = Math.max(30, labelH);
    if (st.comment) neededH += Math.max(14, wrapHeight(doc, `"${st.comment}"`, W - M * 2 - 20, 9));
    if (st.action_required) neededH += Math.max(14, wrapHeight(doc, st.action_required, W - M * 2 - 100, 9));
    if (item.kind === "measurement" && st.value != null && st.value !== "") neededH += 20;
    // Reserve rows of ~160pt for inline evidence photos (2×2 grid so
    // multiple photos wrap onto multiple rows).
    if (Array.isArray(st.__inlinePhotos) && st.__inlinePhotos.length) {
      const rows = Math.ceil(st.__inlinePhotos.length / 2);
      neededH += 24 + rows * 148;
    }
    // Pin matrices always start on a fresh page-band with headroom for the
    // 44-tile grid + summary chips + track id line + spacing.
    if (isPin) {
      const pinRows = Math.ceil((item.pin_count || 44) / 11);
      neededH += pinRows * 22 + 60;
    }
    y = ensureRoom(doc, y, insp, neededH + 12);

    if (isPin) {
      y = drawItemRow(doc, M, y, W, item, st, rowIdx, cItemW, cStatusW, cWhoW, cWhenW);
      rowIdx++;
      y = drawPinMatrix(doc, M, y + 4, W, item, st) + 8;
    } else {
      y = drawItemRow(doc, M, y, W, item, st, rowIdx, cItemW, cStatusW, cWhoW, cWhenW);
      rowIdx++;
      // Optional inline extras — printed under the row so context stays
      // together on the same page. Josh Feb 23: "when there is a issue
      // with something and someone add a photo on the bit of the
      // inspection when you look at PDF can you put under that but not
      // at the end of PDF" → photos also go here, inline, before we
      // move on to the next item.
      if (st.comment) y = drawInlineComment(doc, M, y, W, st.comment);
      if (st.action_required) y = drawInlineAction(doc, M, y, W, st.action_required, st.action_priority);
      if (item.kind === "measurement" && st.value != null && st.value !== "") {
        y = drawInlineMeasurement(doc, M, y, W, item, st);
      }
      if (Array.isArray(st.__inlinePhotos) && st.__inlinePhotos.length) {
        y = drawInlineItemPhotos(doc, M, y, W, insp, st.__inlinePhotos);
      }
    }
  });

  return y + 12;
}

function drawItemRow(doc, M, y, W, item, st, rowIdx, cItemW, cStatusW, cWhoW, cWhenW) {
  const label = item.label || item.name || "—";
  const status = statusFromState(st, item);
  const who = st.clicked_by_name || st.clicked_by || "";
  const when = fmtWhen(st.clicked_at);

  // Alternating background
  const rowH = Math.max(24, wrapHeight(doc, label, cItemW - 16, 9.5));
  if (rowIdx % 2 === 0) {
    doc.setFillColor(250, 250, 250);
    doc.rect(M, y, W - M * 2, rowH, "F");
  }
  doc.setDrawColor(230, 230, 230);
  doc.line(M, y + rowH, W - M * 2 + M, y + rowH);

  // Item
  doc.setTextColor(20, 20, 20);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  const lines = doc.splitTextToSize(label, cItemW - 16);
  lines.forEach((ln, i) => doc.text(ln, M + 8, y + 13 + i * 11));

  // Status column: for measurement / pin-matrix / selector items, show the
  // actual recorded value so the compliance pack reads "165 mm" / "AVG 20°C"
  // just like the mobile app's PDF — not just "OK".
  const kind = item.kind || "check";
  const unit = item.unit || "";
  const wearIsNew = kind === "wear_pct" && String(st.status || "").toLowerCase() === "new";
  const wearPctVal = kind === "wear_pct" && st.status != null && st.status !== "" && !wearIsNew
    ? (Number(st.status) || Number(st.status) === 0 ? Number(st.status) : null)
    : (wearIsNew ? 0 : null);
  const isWearPct = wearIsNew || (wearPctVal != null && !isNaN(wearPctVal));
  const valueLabel = (() => {
    if (isWearPct) {
      return wearIsNew ? "NEW" : `${wearPctVal}%`;
    }
    if (kind === "measurement" && st.value != null && st.value !== "") {
      return `${st.value}${unit ? " " + unit : ""}`;
    }
    if (kind === "pin_matrix" && st.extras?.average != null) {
      return `AVG ${Number(st.extras.average).toFixed(1)}${unit || "°C"}`;
    }
    if (kind === "selector" && (st.value_str || st.value)) {
      return String(st.value_str || st.value).toUpperCase();
    }
    return null;
  })();
  if (valueLabel) {
    const pillStatus = isWearPct ? wearPctKey(wearIsNew ? 0 : wearPctVal) : status;
    drawValuePill(doc, M + cItemW + 8, y + 6, cStatusW - 16, 14, valueLabel, pillStatus);
  } else {
    drawStatusPill(doc, M + cItemW + 8, y + 6, cStatusW - 16, 14, status);
  }

  // Who
  doc.setTextColor(70, 70, 70);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(truncate(doc, who || "—", cWhoW - 12), M + cItemW + cStatusW + 8, y + 15);

  // When
  doc.setTextColor(70, 70, 70);
  doc.text(truncate(doc, when || "—", cWhenW - 12), M + cItemW + cStatusW + cWhoW + 8, y + 15);

  return y + rowH;
}

function drawValuePill(doc, x, y, w, h, label, statusKey) {
  // Prefer the status-tone bg if the tradesperson also picked OK/ISSUE/N/A,
  // otherwise a neutral orange-tinted background so the value visually pops.
  const st = STATUS[statusKey] || { bg: [255, 236, 213], text: [178, 92, 12] };
  doc.setFillColor(...st.bg);
  doc.roundedRect(x, y, w, h, 6, 6, "F");
  doc.setTextColor(...st.text);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  const truncated = truncate(doc, label, w - 8);
  const lw = doc.getTextWidth(truncated);
  doc.text(truncated, x + (w - lw) / 2, y + h - 4);
}

function drawStatusPill(doc, x, y, w, h, statusKey) {
  const st = STATUS[statusKey] || STATUS.open;
  doc.setFillColor(...st.bg);
  doc.roundedRect(x, y, w, h, 6, 6, "F");
  doc.setTextColor(...st.text);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  const label = st.label;
  const lw = doc.getTextWidth(label);
  doc.text(label, x + (w - lw) / 2, y + h - 4);
}

function drawInlineComment(doc, M, y, W, comment) {
  const text = `“${String(comment).trim()}”`;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  const lines = doc.splitTextToSize(text, W - M * 2 - 20);
  lines.forEach((ln) => { doc.text(ln, M + 12, y + 12); y += 12; });
  return y + 4;
}

function drawInlineAction(doc, M, y, W, action, priority) {
  const label = priority ? `ACTION (${String(priority).toUpperCase()})` : "ACTION";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(178, 34, 34);
  doc.text(label, M + 12, y + 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(70, 70, 70);
  const lines = doc.splitTextToSize(String(action), W - M * 2 - 100);
  lines.forEach((ln, i) => doc.text(ln, M + 12 + (i === 0 ? doc.getTextWidth(label) + 8 : 0), y + 12));
  return y + Math.max(14, lines.length * 12) + 2;
}

function drawInlineMeasurement(doc, M, y, W, item, st) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(130, 130, 130);
  doc.text("VALUE", M + 12, y + 12);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(15, 15, 15);
  const unit = item.unit ? ` ${item.unit}` : "";
  doc.text(`${st.value}${unit}`, M + 12 + doc.getTextWidth("VALUE") + 8, y + 12);
  return y + 16;
}

/**
 * drawInlineItemPhotos — renders per-item evidence photos INLINE right
 * under the item row, so the photo sits next to the very issue it
 * documents. Josh Feb 23: "when there is a issue with something and
 * someone add a photo on the bit of the inspection when you look at
 * PDF can you put under that but not at the end of PDF".
 *
 * Layout:
 *   • Small orange "PHOTO EVIDENCE" caps header + count
 *   • 2 photos per row, ~140pt tall each, object-fit letterboxed
 *   • Marked-up shots get an orange MARKED chip on the tile
 *   • Auto page-breaks between rows via ensureRoom
 *
 * `photos` — [{ dataUrl, w, h, annotated }] pre-resolved by the async
 * builder so this function can stay purely synchronous.
 */
function drawInlineItemPhotos(doc, M, y, W, insp, photos) {
  if (!photos?.length) return y;
  const availW = W - M * 2;
  const gap = 6;
  const cols = 2;
  const tileW = (availW - gap * (cols - 1)) / cols;
  const tileH = 140;
  const pad = 4;

  // Header
  y = ensureRoom(doc, y, insp, 22);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(245, 130, 32);
  doc.text(`PHOTO EVIDENCE  ·  ${photos.length}`, M + 4, y + 12);
  doc.setDrawColor(245, 130, 32);
  doc.setLineWidth(0.7);
  doc.line(M + 4, y + 15, M + 76, y + 15);
  y += 20;

  for (let i = 0; i < photos.length; i += cols) {
    y = ensureRoom(doc, y, insp, tileH + 6);
    const row = photos.slice(i, i + cols);
    row.forEach((p, idx) => {
      const x = M + idx * (tileW + gap);
      // Frame
      doc.setDrawColor(210, 210, 210);
      doc.setFillColor(248, 248, 248);
      doc.rect(x, y, tileW, tileH, "F");
      // Fit-inside letterbox — preserve aspect ratio (no crop) so the
      // technician's markings stay geometrically correct.
      const scale = Math.min((tileW - pad * 2) / (p.w || 1), (tileH - pad * 2) / (p.h || 1));
      const dw = (p.w || 800) * scale;
      const dh = (p.h || 600) * scale;
      const dx = x + (tileW - dw) / 2;
      const dy = y + (tileH - dh) / 2;
      try {
        doc.addImage(p.dataUrl, "JPEG", dx, dy, dw, dh, undefined, "FAST");
      } catch { /* unsupported format — leave the frame */ }
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.5);
      doc.rect(x, y, tileW, tileH);
      if (p.annotated) {
        doc.setFillColor(230, 105, 22);
        doc.setDrawColor(230, 105, 22);
        doc.rect(x + 4, y + 4, 44, 12, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(255, 255, 255);
        doc.text("MARKED", x + 6, y + 12.5);
      }
    });
    y += tileH + 6;
  }
  return y + 2;
}


function drawPinMatrix(doc, M, y, W, item, st) {
  const ext = st.extras || {};
  const total = item.pin_count || 44;
  // Values array (mobile app writes extras.values). Fall back to legacy pins dict.
  let values = Array.from({ length: total }, () => null);
  if (Array.isArray(ext.values)) {
    ext.values.forEach((v, i) => { if (i < total) values[i] = v == null || v === "" ? null : Number(v); });
  } else if (ext.pins && typeof ext.pins === "object") {
    Object.entries(ext.pins).forEach(([k, v]) => {
      const idx = Number(k) - 1;
      if (idx >= 0 && idx < total) values[idx] = v == null ? null : Number(v);
    });
  }
  const rules = item.rules?.auto_flag || { ok_max_above_avg: 5, relube_max_above_avg: 10, absolute_ppf: 35 };
  const nums = values.filter((v) => v != null && !isNaN(v));
  const avg = ext.average != null ? Number(ext.average)
    : (nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null);

  // Header — matrix title + summary chips
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  // Prefer the item's actual label (e.g. "Left Track Pin Temperatures (Pin 1
  // -> 44)") so the compliance PDF makes it unmistakable which set of pins
  // this grid belongs to. Fall back to the generic label only for legacy
  // items without an explicit title.
  const gridTitle = item.label
    ? `${item.label}${item.unit ? ` · ${item.unit}` : ""}`
    : `Pin Temperatures · ${total} pins${item.unit ? ` (${item.unit})` : ""}`;
  doc.text(gridTitle, M + 8, y + 12);
  const summary = [
    { label: "AVG", value: avg != null ? `${avg.toFixed(1)}${item.unit || "°C"}` : "—" },
    { label: "OK",     value: num(ext.ok_count) },
    { label: "RELUBE", value: num(ext.relube_count) },
    { label: "FAIL",   value: num(ext.fail_count) },
  ];
  let sx = W - M - 8;
  for (let i = summary.length - 1; i >= 0; i--) {
    const s = summary[i];
    const text = `${s.label} ${s.value}`;
    const tw = doc.getTextWidth(text) + 14;
    sx -= tw + 6;
    doc.setFillColor(248, 248, 248);
    doc.setDrawColor(220, 220, 220);
    doc.roundedRect(sx, y + 2, tw, 14, 4, 4, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(60, 60, 60);
    doc.text(text, sx + 7, y + 12);
  }
  y += 20;

  // Track ID
  if (ext.track_id) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Track ID: ${ext.track_id}`, M + 8, y + 4);
    y += 8;
  }

  // Grid
  const cols = 11;
  const rows = Math.ceil(total / cols);
  const gridW = W - M * 2 - 4;
  const cellW = gridW / cols;
  const cellH = 22;
  values.forEach((v, i) => {
    const r = Math.floor(i / cols);
    const c = i % cols;
    const x = M + 2 + c * cellW;
    const yy = y + r * cellH + 4;
    const flagKey = pinFlag(v, avg, rules);
    const tone = TEMP[flagKey] || TEMP.empty;
    doc.setFillColor(...tone.bg);
    doc.setDrawColor(220, 220, 220);
    doc.roundedRect(x + 1, yy + 1, cellW - 2, cellH - 2, 3, 3, "F");
    doc.setTextColor(...tone.text);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.text(String(i + 1), x + 4, yy + 8);
    doc.setFontSize(9);
    const val = v == null ? "—" : String(v);
    doc.text(val, x + cellW - 4 - doc.getTextWidth(val), yy + 15);
  });
  return y + rows * cellH + 6;
}

function drawSignatureBlock(doc, M, y, W, insp, rasterized = {}, labels = {}) {
  const boxW = (W - M * 2 - 20) / 2;
  const boxH = 110;
  drawSigBox(
    doc, M, y, boxW, boxH,
    labels.leftLabel || "TRADESPERSON",
    rasterized.trade,
    insp.tradesperson_sig,
    insp.started_by_name || insp.started_by,
    insp.started_at,
  );
  drawSigBox(
    doc, M + boxW + 20, y, boxW, boxH,
    labels.rightLabel || "SUPERVISOR",
    rasterized.sup,
    insp.supervisor_sig,
    insp.supervisor_name,
    insp.approved_at || insp.updated_at,
  );
  return y + boxH + 12;
}

function drawSigBox(doc, x, y, w, h, roleLabel, raster, sigRaw, name, when) {
  doc.setDrawColor(210, 210, 210);
  doc.roundedRect(x, y, w, h, 8, 8);
  doc.setTextColor(140, 140, 140);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(roleLabel, x + 10, y + 14);

  // Prefer the pre-rasterised PNG (works for SVG sigs the mobile app writes).
  const imgW = w - 24;
  const imgH = h - 58;
  let drew = false;
  if (raster && raster.dataUrl) {
    try {
      doc.addImage(raster.dataUrl, "PNG", x + 12, y + 20, imgW, imgH);
      drew = true;
    } catch { /* fall through */ }
  } else if (sigRaw && typeof sigRaw === "string" && sigRaw.startsWith("data:image")) {
    // Data-URL PNG/JPEG (older portal-generated sigs).
    try {
      doc.addImage(sigRaw, "PNG", x + 12, y + 20, imgW, imgH);
      drew = true;
    } catch { /* fall through */ }
  }
  if (!drew) {
    doc.setTextColor(190, 190, 190);
    doc.setFontSize(10);
    doc.text("— unsigned —", x + 12, y + (h / 2));
  }

  // Name + date at the bottom
  doc.setTextColor(20, 20, 20);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(name || "—", x + 12, y + h - 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(fmt(when) || "—", x + 12, y + h - 8);
}

// =========================================================================
// HELPERS
// =========================================================================

function ensureRoom(doc, y, insp, needed, logo = null) {
  const H = doc.internal.pageSize.getHeight();
  if (y + needed < H - 70) return y;
  doc.addPage();
  drawWatermark(doc);
  drawHeader(doc, insp, logo || doc.__slLogo || null);
  return CONTENT_TOP;
}

function statusFromState(st, item) {
  const s = String(st?.status || "").toLowerCase();
  if (s === "ok") return "ok";
  if (s === "issue") {
    if (st?.action_priority) return "ppf";
    return "issue";
  }
  if (s === "na" || s === "n/a") return "na";
  if (s === "ppf") return "ppf";
  if (s === "75" || s === "wear75" || s === "75%") return "wear75";
  if (s === "co" || s === "c/o" || s === "changeout") return "co";
  // Item-kind fallbacks
  if ((item?.kind === "measurement" || item?.kind === "pin_matrix") && st?.value != null && st?.value !== "") return "ok";
  return "open";
}

function pinFlag(v, avg, rules) {
  if (v == null || isNaN(v)) return "empty";
  const n = Number(v);
  if (n >= rules.absolute_ppf) return "fail";
  if (avg == null) return "ok";
  const d = n - avg;
  if (d >= rules.relube_max_above_avg) return "fail";
  if (d >= rules.ok_max_above_avg) return "relube";
  return "ok";
}

function wearPctKey(pct) {
  if (pct == null) return "open";
  const n = Number(pct);
  if (n >= 100) return "co";       // change-out
  if (n >= 75)  return "wear75";
  if (n >= 50)  return "issue";
  return "ok";
}

function titleLine(insp) {
  const tpl = String(insp.template_name || "Inspection").toUpperCase();
  const asset = insp.asset_id || insp.asset_name || "";
  return asset ? `${tpl} — ${asset}` : tpl;
}

function num(v) { const n = Number(v || 0); return isFinite(n) ? n : 0; }
function cap(s) { const t = String(s || ""); return t ? t.charAt(0).toUpperCase() + t.slice(1) : ""; }
function fmt(v) {
  if (!v) return "";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}
function truncate(doc, str, maxW) {
  if (!str) return "";
  str = sanitize(str);
  if (doc.getTextWidth(str) <= maxW) return str;
  while (doc.getTextWidth(str + "…") > maxW && str.length > 1) str = str.slice(0, -1);
  return str + "…";
}
function wrapHeight(doc, str, maxW, fontSize) {
  const prev = doc.getFontSize();
  doc.setFontSize(fontSize);
  const lines = doc.splitTextToSize(sanitize(str), maxW);
  doc.setFontSize(prev);
  return Math.max(24, lines.length * 11 + 12);
}

/**
 * Replace glyphs helvetica (the default jsPDF font) can't render.
 * Keeps text readable at print time without embedding a heavy unicode font.
 */
function sanitize(str) {
  if (str == null) return "";
  return String(str)
    .replace(/[→⇒➜➔➤►▶]/g, "->")
    .replace(/[←⇐]/g, "<-")
    .replace(/[↔]/g, "<->")
    .replace(/[·•]/g, "\u2022")   // keep bullet — jsPDF supports 0x2022
    .replace(/[…]/g, "...")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[–—]/g, "-");
}

/**
 * Monkey-patch doc.text and doc.splitTextToSize once so every string
 * passing through gets sanitized. Cheaper than dotting sanitize() across
 * 30+ call sites and impossible to forget.
 */
function wrapWithSanitizer(doc) {
  const origText = doc.text.bind(doc);
  doc.text = function patchedText(text, x, y, options) {
    if (Array.isArray(text)) text = text.map(sanitize);
    else text = sanitize(text);
    return origText(text, x, y, options);
  };
  const origSplit = doc.splitTextToSize.bind(doc);
  doc.splitTextToSize = function patchedSplit(text, ...rest) {
    return origSplit(sanitize(text), ...rest);
  };
  const origWidth = doc.getTextWidth.bind(doc);
  doc.getTextWidth = function patchedWidth(text) {
    return origWidth(sanitize(text));
  };
}
